import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { Card, Row, Col, Button, Spinner, ProgressBar } from 'react-bootstrap';
import ReporteAlertasFiltros from './ReporteAlertasFiltros';
import { toast } from "react-toastify";
import ReporteAlertasFiltrosOmi from './ReporteAlertasFiltrosOmi';
import { AlertaService } from '../../../services/apiService';
import AlertasDetonadas from './components/alertas-detonadas/AlertasDetonadas';
import AlertaVehiculo from './components/alertas-vehiculo/AlertaVehiculo';
import AlertaTableList from './components/alertas-listado-quro/AlertaTableList';
import AlertaUbicacion from './components/alertas-ubicaciones/AlertaUbicacion';
import AlertaFrecuencia from './components/alertas-frecuencia/AlertaFrecuencia';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import DebugMetricsPanel from '../../../components/common/DebugMetricsPanel';
import cargarPaginas, { cargarPaginasConMetricas } from '../../../providers/provider_paginado';
import { limpiarCache } from '../../../providers/provider_paginado';
import { paginadorAlertasAfterStore, paginadorAlertasBeforeStore, paginadorAlertasStore } from '../../../lib/store/paginador_store';
import { jwtDecode } from 'jwt-decode';
import Flex from '../../../components/common/Flex';
import classNames from 'classnames';
import AlertaTableListTanstack from './components/alertas-listado-quro/AlertaTableListTanstack';
import '../../../styles/global.css';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useUrlParams } from '../../../hooks/useUrlParams';
import StandardProgressBar from '../../../components/common/ProgressBar/StandardProgressBar';

import SmartComponentRenderer from '../../../components/loading/SmartComponentRenderer';
import { cargarPaginasUltraOptimizado } from '../../../providers/provider_paginado';
import { AuthContext } from '../../../contexts/AuthContext';

import {
    ProgressBarWithRealTimeMetrics,
    EmptyState,
    WelcomeEmptyState,
    LoadingEmptyState,
    NoDataEmptyState,
    NoSelectionEmptyState,
    ProgressBarWithComponents,
    AlertError,
    APIError,
    ValidationError
} from '../../../components/common/progressemptystate.js';
import alertasFiltrosStore from '../../../lib/store/alertas_filtros_store.js';
import AnimatedComponentWrapper from '../../../components/loading/AnimatedComponentWrapper.js';

import PrintReportModule from '../../../components/print/PrintReportModule';


// Activamos el soporte para formatos personalizados
dayjs.extend(customParseFormat);
dayjs.locale('es');

const fadeInStyle = {
    animation: 'fadeIn 0.5s ease-in-out',
    '@keyframes fadeIn': {
        '0%': { opacity: 0, transform: 'translateY(20px)' },
        '100%': { opacity: 1, transform: 'translateY(0)' }
    }
};


const PER_PAGE = 100;
const UID_EMPRESAS_EXTENDED_COLUMNS = new Set([
    '5222512e-1ade-4bcc-ba1c-a0e026e87ded',
    '3354e4d7-d85b-4978-9fd8-facebf82e00b'
]);
const EMPRESAS_EXTENDED_COLUMNS = new Set(['153', '614']);

const formatSecondsToHHMMSS = (totalSeconds) => {
    const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
};

const buildAlertDurationKeys = (alert) => {
    let timestamp = dayjs(alert.fecha);
    if (!timestamp.isValid()) {
        timestamp = dayjs(alert.fecha, 'YYYY-MM-DD HH:mm:ss', true);
    }
    if (!timestamp.isValid()) {
        timestamp = dayjs(alert.fecha, 'DD/MM/YYYY HH:mm:ss', true);
    }
    const baseParts = [
        (alert.no_economico || alert.uid_vehiculo || '').toString().toLowerCase().trim(),
        (alert.alerta_grupo || '').toString().toLowerCase().trim(),
        (alert.uid_vehiculo || '').toString().toLowerCase().trim()
    ];
    const baseKey = baseParts.join('|');
    const timeKey = `${baseKey}|${timestamp.isValid() ? timestamp.valueOf() : ''}`;
    return { baseKey, timeKey, timestamp };
};

const computeClosureDurations = (alertsList) => {
    const openings = new Map();
    const durations = new Map();
    const sortedAlerts = [...alertsList].sort((a, b) => {
        const aTime = dayjs(a.fecha).valueOf();
        const bTime = dayjs(b.fecha).valueOf();
        return aTime - bTime;
    });

    for (const alert of sortedAlerts) {
        const normalizedAlert = (alert.alerta || '').toLowerCase();
        if (!normalizedAlert) continue;

        const { baseKey, timeKey, timestamp } = buildAlertDurationKeys(alert);
        if (!timestamp.isValid() || !baseKey) continue;

        const isOpening = normalizedAlert.includes('abiert');
        const isClosing = normalizedAlert.includes('cerrad');

        if (isOpening) {
            openings.set(baseKey, timestamp);
            continue;
        }

        if (isClosing) {
            const startTime = openings.get(baseKey);
            if (!startTime) continue;

            const diffSeconds = timestamp.diff(startTime, 'second');
            if (diffSeconds >= 0) {
                durations.set(timeKey, formatSecondsToHHMMSS(diffSeconds));
                openings.delete(baseKey);
            }
        }
    }

    return durations;
};

const attachClosureDurations = (alertsList) => {
    if (!Array.isArray(alertsList) || alertsList.length === 0) {
        return alertsList;
    }

    const durations = computeClosureDurations(alertsList);
    return alertsList.map(alert => {
        const { timeKey, timestamp } = buildAlertDurationKeys(alert);
        if (!timestamp.isValid()) {
            return { ...alert, tiempo_abierto: null };
        }

        const duration = durations.get(timeKey) || null;
        return {
            ...alert,
            tiempo_abierto: duration
        };
    });
};

const stripClosureDurations = (alertsList) => {
    if (!Array.isArray(alertsList) || alertsList.length === 0) {
        return alertsList;
    }

    return alertsList.map(alert => {
        if (Object.prototype.hasOwnProperty.call(alert, 'tiempo_abierto')) {
            const { tiempo_abierto, ...rest } = alert;
            return rest;
        }
        return alert;
    });
};

const ReporteAlertas = () => {
    // ============================================================================
    // STORES Y PAGINADORES
    // ============================================================================
    const authContext = useContext(AuthContext);
    const uidEmpresa = authContext?.uidEmpresa ?? null;
    const empresaId = authContext?.empresaId ?? localStorage.getItem('empresa_id');
    const showExtendedColumns = UID_EMPRESAS_EXTENDED_COLUMNS.has(`${uidEmpresa ?? ''}`)
        || EMPRESAS_EXTENDED_COLUMNS.has(`${empresaId ?? ''}`);

    const store = alertasFiltrosStore();
    const paginadorAlertas = paginadorAlertasStore();
    const paginadorAlertasAfter = paginadorAlertasAfterStore();
    const paginadorAlertasBefore = paginadorAlertasBeforeStore();
    const [totalCarga, editarTotalCarga] = useState([]);

    const prevDP = {
        title: '',
        percent: 0,
        color: "#ffffff",
        animated: true
    };
    const [totalmapeoFrecuenciaAfter, editarTotalMapeoFrecuenciaAfter] = useState(prevDP);
    const [totalmapeoFrecuenciaBefore, editarTotalMapeoFrecuenciaBefore] = useState(prevDP);
    const [totalCargaFrecuenciaAfter, editarTotalCargaFrecuenciaAfter] = useState(prevDP);
    const [totalCargaFrecuenciaBefore, editarTotalCargaFrecuenciaBefore] = useState(prevDP);

    // ============================================================================
    // ESTADOS PRINCIPALES
    // ============================================================================
    const [showFilters, setShowFilters] = useState(true);
    const [alerts, setAlerts] = useState([]);
    const [alertsAfter, setAlertsAfter] = useState([]);
    const [alertsBefore, setAlertsBefore] = useState([]);
    const [tipoReporte, setTipoReporte] = useState('gps');
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [searchInitiated, setSearchInitiated] = useState(false);
    const [verDetalle, setVerDetalle] = useState(true);

    // ============================================================================
    // ESTADOS DE PAGINACIÓN CLIENT-SIDE
    // ============================================================================
    const [clientSideTableData, setClientSideTableData] = useState([]);
    const [clientSideTotal, setClientSideTotal] = useState(0);
    const [clientSideTotalPages, setClientSideTotalPages] = useState(1);
    const [pagesCache, setPagesCache] = useState({});
    const [isPageChanging, setIsPageChanging] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_PAGE);
    const [maxProgress, setMaxProgress] = useState(0);

    //const { getParam, clearParam, hasParams } = useUrlParams('ReporteAlertas');

    // ============================================================================
    // ESTADOS DE DATOS PROCESADOS
    // ============================================================================
    const [workerData, setWorkerData] = useState({
        tableData: [],
        chartData: [],
        mapData: [],
        total: 0,
        totalPages: 0,
        chartDataGeneral: [],
        totalGeneral: 0,
        frecuenciaData: [],
        frecuenciaDataGeneral: [],
        vehiculoDataGeneral: [],
        filteredData: [],
        frecuenciaHoraDataGeneral: [],
        frecuenciaDataAfter: [],
        frecuenciaHoraDataAfter: [],
        frecuenciaDataBefore: [],
        frecuenciaHoraDataBefore: [],
        isSingleDay: false,
        isSingleDayAfter: false,
        isSingleDayBefore: false,
        totalAfterGeneral: 0,
        totalBeforeGeneral: 0,
    });

    // ============================================================================
    // ESTADOS DE FILTROS
    // ============================================================================
    const [filters, setFilters] = useState({
        fecha_inicio: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        fecha_fin: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
        uids_vehiculo: [],
        ids_tipo_alerta: [],
        uids_evento: [],
        tipo_reporte: "gps",
        tiempo: 0,
        veces: 0,
        velocidad: 0,
        distancia: 0,
        tipo_marcado: 0
    });

    // ============================================================================
    // ESTADOS DE RESALTADO Y SELECCIÓN
    // ============================================================================
    const [highlightedVehicle, setHighlightedVehicle] = useState(null);
    const [highlightedAlertType, setHighlightedAlertType] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedAlertType, setSelectedAlertType] = useState(null);

    // ============================================================================
    // ESTADOS DE CARGA
    // ============================================================================
    const [loadingStates, setLoadingStates] = useState({
        table: false,
        charts: false,
        maps: false
    });

    const [loadingComponents, setLoadingComponents] = useState({
        table: { loading: false, progress: 0, complete: false },
        vehicles: { loading: false, progress: 0, complete: false },
        alerts: { loading: false, progress: 0, complete: false },
        map: { loading: false, progress: 0, complete: false },
        frequency: { loading: false, progress: 0, complete: false }
    });

    const [loadingComponentsFrequency, setLoadingComponentsFrequency] = useState({
        'Periodo Actual': { loading: false, progress: 0, complete: false },
        'Periodo Anterior': { loading: false, progress: 0, complete: false }
    });

    const agregarAlertaTitulo = (alert) => {
        if (tipoReporte === 'gps') {
            let alerta = store.alertas.find(item => item.label.toLowerCase() === alert.alerta.toLowerCase());
            let titulo = alerta?.alerta_grupo?.find(item => item.tipo_alerta === parseInt(alert.tipo_real))?.titulo;
            return {
                ...alert,
                alerta: titulo,
                alerta_grupo: alert.alerta
            }
        }
        return {
            ...alert,
            alerta: alert.alerta_real,
            alerta_grupo: alert.alerta
        }
    };
    // ============================================================================
    // REFS Y UTILIDADES
    // ============================================================================
    const safetyTimeoutRef = useRef(null);
    const monitorIntervalRef = useRef(null);
    const alertColorsRef = useRef(new Map());
    const usedColors = new Set();
    const urlParamsProcessedRef = useRef(false);

    const location = useLocation();
    const { getParam, clearParam, hasParams } = useUrlParams('ReporteAlertas');
    const [initialReportType, setInitialReportType] = useState(null);
    // ============================================================================
    // Constantes para los errores
    // ============================================================================

    const [apiError, setApiError] = useState(null);
    const [validationError, setValidationError] = useState(null);

    // ✅ FUNCIÓN PARA LIMPIAR ERRORES
    const clearErrors = useCallback(() => {
        setApiError(null);
        setValidationError(null);
        setErrorMessage(null);
    }, []);

    // ============================================================================

    const [debugMetrics, setDebugMetrics] = useState({
        searchStartTime: null,
        searchEndTime: null,

        connections: {
            'alertas-gps': {
                name: 'Datos Actuales (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-gps-after': {
                name: 'Datos Históricos (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-gps-before': {
                name: 'Datos Históricos (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara': {
                name: 'Datos Actuales (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara-after': {
                name: 'Datos Históricos (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara-before': {
                name: 'Datos Históricos (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },/********* */
            'alertas-omisiones': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-omisiones-after': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-omisiones-before': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
        },


        globalTotalRecordsExpected: 0,
        globalTotalRecordsReceived: 0,
        globalDownloadProgress: 0,
        activeConnections: 0,


        componentTimes: {
            table: { start: null, end: null },
            vehicles: { start: null, end: null },
            alerts: { start: null, end: null },
            map: { start: null, end: null },
            frequency: { start: null, end: null }
        }
    });

    const updateDebugMetrics = useCallback((updates) => {
        setDebugMetrics(prev => {

            if (!prev || !prev.connections) {
                console.warn('⚠️ debugMetrics no está inicializado correctamente');
                return prev || {
                    connections: {},
                    globalTotalRecordsExpected: 0,
                    globalTotalRecordsReceived: 0,
                    globalDownloadProgress: 0,
                    activeConnections: 0,
                    componentTimes: {}
                };
            }
            const newState = { ...prev };

            if (updates.connectionId && newState.connections[updates.connectionId]) {
                const connection = newState.connections[updates.connectionId];


                if (!connection.apiCallsHistory) {
                    connection.apiCallsHistory = [];
                }

                // Actualizar datos específicos de la conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        // ✅ VALIDACIÓN ESPECIAL PARA apiCallsHistory
                        if (key === 'apiCallsHistory' && Array.isArray(updates[key])) {
                            // Agregar nuevas llamadas al historial existente
                            const newCalls = updates[key].map(call => ({
                                ...call,
                                timestamp: call.timestamp || Date.now()
                            }));
                            connection[key] = [...(connection[key] || []), ...newCalls];

                            // Mantener solo las últimas 50 llamadas para evitar memoria excesiva
                            if (connection[key].length > 50) {
                                connection[key] = connection[key].slice(-50);
                            }
                        } else if (key !== 'apiCallsHistory') {
                            connection[key] = updates[key];
                        }
                    }
                });

                const activeConnections = Object.values(newState.connections).filter(conn =>
                    conn && (conn.status === 'downloading' || conn.status === 'completed')
                );

                newState.globalTotalRecordsExpected = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsExpected || 0), 0
                );

                newState.globalTotalRecordsReceived = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsReceived || 0), 0
                );

                newState.globalDownloadProgress = newState.globalTotalRecordsExpected > 0 ?
                    (newState.globalTotalRecordsReceived / newState.globalTotalRecordsExpected * 100) : 0;

                newState.activeConnections = activeConnections.length;

            } else if (updates.connectionId && !newState.connections[updates.connectionId]) {

                newState.connections[updates.connectionId] = {
                    name: updates.connectionId,
                    startTime: null,
                    endTime: null,
                    totalRecordsExpected: 0,
                    totalRecordsReceived: 0,
                    totalApiCalls: 0,
                    completedApiCalls: 0,
                    failedApiCalls: 0,
                    recoveredApiCalls: 0,
                    apiCallsHistory: [],
                    downloadProgress: 0,
                    status: 'pending'
                };

                // Aplicar las actualizaciones a la nueva conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        newState.connections[updates.connectionId][key] = updates[key];
                    }
                });

            } else {
                // ✅ ACTUALIZACIONES GLOBALES CON VALIDACIÓN
                Object.keys(updates).forEach(key => {
                    if (key === 'componentTimes') {
                        if (!newState.componentTimes) {
                            newState.componentTimes = {};
                        }
                        newState.componentTimes = { ...newState.componentTimes, ...updates.componentTimes };
                    } else if (key !== 'connectionId') {
                        newState[key] = updates[key];
                    }
                });
            }

            return newState;
        });
    }, []);

    const [debugMetricsFrequency, setDebugMetricsFrequency] = useState({
        searchStartTime: null,
        searchEndTime: null,

        connections: {
            'alertas-gps': {
                name: 'Datos Actuales (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-gps-after': {
                name: 'Datos Históricos (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-gps-before': {
                name: 'Datos Históricos (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara': {
                name: 'Datos Actuales (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara-after': {
                name: 'Datos Históricos (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-camara-before': {
                name: 'Datos Históricos (IA)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },/********* */
            'alertas-omisiones': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-omisiones-after': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
            'alertas-omisiones-before': {
                name: 'Datos Históricos (Omisiones)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            },
        },


        globalTotalRecordsExpected: 0,
        globalTotalRecordsReceived: 0,
        globalDownloadProgress: 0,
        activeConnections: 0,


        componentTimes: {
            'Periodo Actual': { start: null, end: null },
            'Periodo Anterior': { start: null, end: null },
        }
    });

    const updateDebugMetricsFrequency = useCallback((updates) => {
        setDebugMetricsFrequency(prev => {

            if (!prev || !prev.connections) {
                console.warn('⚠️ debugMetrics no está inicializado correctamente');
                return prev || {
                    connections: {},
                    globalTotalRecordsExpected: 0,
                    globalTotalRecordsReceived: 0,
                    globalDownloadProgress: 0,
                    activeConnections: 0,
                    componentTimes: {}
                };
            }
            const newState = { ...prev };

            if (updates.connectionId && newState.connections[updates.connectionId]) {
                const connection = newState.connections[updates.connectionId];


                if (!connection.apiCallsHistory) {
                    connection.apiCallsHistory = [];
                }

                // Actualizar datos específicos de la conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        // ✅ VALIDACIÓN ESPECIAL PARA apiCallsHistory
                        if (key === 'apiCallsHistory' && Array.isArray(updates[key])) {
                            // Agregar nuevas llamadas al historial existente
                            const newCalls = updates[key].map(call => ({
                                ...call,
                                timestamp: call.timestamp || Date.now()
                            }));
                            connection[key] = [...(connection[key] || []), ...newCalls];

                            // Mantener solo las últimas 50 llamadas para evitar memoria excesiva
                            if (connection[key].length > 50) {
                                connection[key] = connection[key].slice(-50);
                            }
                        } else if (key !== 'apiCallsHistory') {
                            connection[key] = updates[key];
                        }
                    }
                });

                const activeConnections = Object.values(newState.connections).filter(conn =>
                    conn && (conn.status === 'downloading' || conn.status === 'completed')
                );

                newState.globalTotalRecordsExpected = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsExpected || 0), 0
                );

                newState.globalTotalRecordsReceived = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsReceived || 0), 0
                );

                newState.globalDownloadProgress = newState.globalTotalRecordsExpected > 0 ?
                    (newState.globalTotalRecordsReceived / newState.globalTotalRecordsExpected * 100) : 0;

                newState.activeConnections = activeConnections.length;

            } else if (updates.connectionId && !newState.connections[updates.connectionId]) {

                newState.connections[updates.connectionId] = {
                    name: updates.connectionId,
                    startTime: null,
                    endTime: null,
                    totalRecordsExpected: 0,
                    totalRecordsReceived: 0,
                    totalApiCalls: 0,
                    completedApiCalls: 0,
                    failedApiCalls: 0,
                    recoveredApiCalls: 0,
                    apiCallsHistory: [],
                    downloadProgress: 0,
                    status: 'pending'
                };

                // Aplicar las actualizaciones a la nueva conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        newState.connections[updates.connectionId][key] = updates[key];
                    }
                });

            } else {
                // ✅ ACTUALIZACIONES GLOBALES CON VALIDACIÓN
                Object.keys(updates).forEach(key => {
                    if (key === 'componentTimes') {
                        if (!newState.componentTimes) {
                            newState.componentTimes = {};
                        }
                        newState.componentTimes = { ...newState.componentTimes, ...updates.componentTimes };
                    } else if (key !== 'connectionId') {
                        newState[key] = updates[key];
                    }
                });
            }

            return newState;
        });
    }, []);

    const [showDebugPanel, setShowDebugPanel] = useState(false);


    useEffect(() => {
        if (urlParamsProcessedRef.current) {
            return;
        }

        const currentSearchParams = new URLSearchParams(location.search);
        const reportTypeFromUrl = currentSearchParams.get('report_type') || currentSearchParams.get('tipo_reporte');

        if (reportTypeFromUrl) {
            setInitialReportType(reportTypeFromUrl);
            setTipoReporte(reportTypeFromUrl);

            urlParamsProcessedRef.current = true;

            setTimeout(() => {
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.delete('report_type');
                newSearchParams.delete('tipo_reporte');

                const newSearch = newSearchParams.toString();
                if (newSearch !== location.search.substring(1)) {
                    window.history.replaceState({}, '', `${location.pathname}${newSearch ? '?' + newSearch : ''}`);

                }
            }, 500);
        } else {
            urlParamsProcessedRef.current = true;
        }
    }, [location.search]);

    //impresión del reporte
    useEffect(() => {
        if (!window.printReportModule) {

            window.printReportModule = new PrintReportModule({
                captureQuality: 0.95,
                pdfFormat: 'A4',
                pdfOrientation: 'portrait',

                tableExtractionConfig: {
                    enabled: true,
                    selector: '#tabla-bitacoras',
                    title: 'Listado Bitácora',
                    type: 'auto' // ✅ AHORA DETECTARÁ BitacoraTableListTanstack AUTOMÁTICAMENTE
                },
            });
        }

        return () => {
            if (window.printReportModule) {
                delete window.printReportModule;
            }
        };
    }, []);

    // ============================================================================
    // FUNCIONES DE COLORES
    // ============================================================================
    const getUniqueColor = useCallback(() => {
        let newColor;
        do {
            newColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
        } while (usedColors.has(newColor));
        usedColors.add(newColor);
        return newColor;
    }, [usedColors]);

    const getAlertColor = useCallback((alertType) => {
        if (!alertColorsRef.current.has(alertType)) {
            const newColor = getUniqueColor();
            alertColorsRef.current.set(alertType, newColor);
        }
        return alertColorsRef.current.get(alertType);
    }, [getUniqueColor, alertColorsRef]);

    // ============================================================================
    // MEMOS COMPUTADOS
    // ============================================================================
    const tableData = useMemo(() => {
        const baseData = workerData.filteredData || [];
        if (showExtendedColumns) {
            return baseData;
        }
        return stripClosureDurations(baseData);
    }, [workerData.filteredData, showExtendedColumns]);

    const columns = useMemo(() => {
        const baseColumns = [
            { id: 'fecha', Header: 'Fecha', accessor: 'fecha', header: 'Fecha', accessorKey: 'fecha' },
            { id: 'alerta', Header: 'Alerta', accessor: 'alerta', header: 'Alerta', accessorKey: 'alerta' },
            { id: 'no_economico', Header: 'Vehículo', accessor: 'no_economico', header: 'Vehículo', accessorKey: 'no_economico' }
        ];

        if (showExtendedColumns) {
            baseColumns.push(
                { id: 'random', Header: 'Random', accessor: 'random', header: 'Random', accessorKey: 'random' },
                { id: 'tiempo_abierto', Header: 'Tiempo abierto', accessor: 'tiempo_abierto', header: 'Tiempo abierto', accessorKey: 'tiempo_abierto' }
            );
        }

        baseColumns.push(
            { id: 'chofer', Header: 'Chofer', accessor: 'chofer', header: 'Chofer', accessorKey: 'chofer' },
            { id: 'geocerca', Header: 'Geocerca', accessor: 'geocerca', header: 'Geocerca', accessorKey: 'geocerca' },
            { id: 'coordenadas', Header: 'Coordenadas', accessor: 'coordenadas', header: 'Coordenadas', accessorKey: 'coordenadas' },
            { id: 'ubicacion', Header: 'Mapa', accessor: 'ubicacion', header: 'Mapa', accessorKey: 'ubicacion' },
            { id: 'video', Header: 'Video', accessor: 'video', header: 'Video', accessorKey: 'video' }
        );

        return baseColumns;
    }, [showExtendedColumns]);

    // ============================================================================
    // FUNCIONES DE PROGRESO
    // ============================================================================
    const updateComponentProgress = useCallback((component, progress, isComplete = false) => {
        if (!component || typeof component !== 'string') {
            console.error("Componente inválido:", component);
            return;
        }
        setLoadingComponents(prevState => {
            if (prevState[component]?.complete && isComplete) {
                return prevState;
            }

            const newState = { ...prevState };
            newState[component] = {
                loading: !isComplete,
                progress: progress || 0,
                complete: isComplete
            };

            // ✅ ACTUALIZAR MÉTRICAS DE TIEMPO
            const currentTime = Date.now();
            if (!prevState[component]?.loading && !isComplete && progress > 0) {
                // Componente iniciando

                updateDebugMetrics({
                    componentTimes: {
                        [component]: { start: currentTime, end: null }
                    }
                });
            } else if (isComplete) {
                // Componente completado

                updateDebugMetrics({
                    componentTimes: {
                        [component]: {
                            start: debugMetrics.componentTimes[component]?.start || currentTime,
                            end: currentTime
                        }
                    }
                });
            }
            return newState;
        });
    }, [updateDebugMetrics, debugMetrics.componentTimes]);

    const updateComponentFrequencyProgress = useCallback((component, progress, isComplete = false) => {
        if (!component || typeof component !== 'string') {
            console.error("Componente inválido:", component);
            return;
        }
        setLoadingComponentsFrequency(prevState => {
            if (prevState[component]?.complete && isComplete) {
                return prevState;
            }

            const newState = { ...prevState };
            newState[component] = {
                loading: !isComplete,
                progress: progress || 0,
                complete: isComplete
            };

            // ✅ ACTUALIZAR MÉTRICAS DE TIEMPO
            const currentTime = Date.now();
            if (!prevState[component]?.loading && !isComplete && progress > 0) {
                // Componente iniciando

                updateDebugMetricsFrequency({
                    componentTimes: {
                        [component]: { start: currentTime, end: null }
                    }
                });
            } else if (isComplete) {
                // Componente completado

                updateDebugMetricsFrequency({
                    componentTimes: {
                        [component]: {
                            start: debugMetricsFrequency.componentTimes[component]?.start || currentTime,
                            end: currentTime
                        }
                    }
                });
            }
            return newState;
        });
    }, [updateDebugMetricsFrequency, debugMetrics.componentTimes]);

    const calculateTotalProgress = useCallback(() => {
        const weights = {
            table: 0.35,
            vehicles: 0.2,
            alerts: 0.2,
            map: 0.15,
            frequency: 0.1
        };

        let totalProgress = 0;
        let totalWeight = 0;

        Object.entries(loadingComponents).forEach(([component, state]) => {
            if (state && (state.loading || state.complete)) {
                const progress = Number(state.progress);
                if (!isNaN(progress)) {
                    totalProgress += progress * weights[component];
                    totalWeight += weights[component];
                } else {
                    totalProgress += (state.complete ? 100 : 0) * weights[component];
                    totalWeight += weights[component];
                }
            }
        });

        if (totalWeight === 0) return 0;
        const result = totalProgress / totalWeight;

        // console.log('🎨 Progreso de UI:', {
        //     componentes: Object.keys(loadingComponents).map(k => ({
        //         [k]: `${loadingComponents[k].progress}%`
        //     })),
        //     total: result.toFixed(1)
        // });

        return Math.min(Math.max(result, 0), 100);
    }, [loadingComponents]);

    const calculateTotalProgressFrequency = useCallback(() => {
        const weights = {
            'Periodo Actual': .5,
            'Periodo Anterior': .5
        };

        let totalProgress = 0;
        let totalWeight = 0;

        Object.entries(loadingComponentsFrequency).forEach(([component, state]) => {
            if (state && (state.loading || state.complete)) {
                const progress = Number(state.progress);
                if (!isNaN(progress)) {
                    totalProgress += progress * weights[component];
                    totalWeight += weights[component];
                } else {
                    totalProgress += (state.complete ? 100 : 0) * weights[component];
                    totalWeight += weights[component];
                }
            }
        });

        if (totalWeight === 0) return 0;
        const result = totalProgress / totalWeight;

        // console.log('🎨 Progreso de UI:', {
        //     componentes: Object.keys(loadingComponents).map(k => ({
        //         [k]: `${loadingComponents[k].progress}%`
        //     })),
        //     total: result.toFixed(1)
        // });

        return Math.min(Math.max(result, 0), 100);
    }, [loadingComponentsFrequency]);
    // ============================================================================
    // FUNCIONES DE ESTADO
    // ============================================================================
    const shouldShowEmptyState = useCallback(() => {
        if (isLoading) return false;

        if (!filters ||
            !filters.uids_vehiculo ||
            filters.uids_vehiculo.length === 0 ||
            !filters.ids_tipo_alerta ||
            filters.ids_tipo_alerta.length === 0) {
            return true;
        }

        if (alerts.length === 0 && !paginadorAlertas.cargando) {
            return true;
        }

        return false;
    }, [isLoading, filters, alerts.length, paginadorAlertas.cargando]);



    const getEmptyStateProps = useCallback(() => {
        if (!filters ||
            !filters.uids_vehiculo ||
            filters.uids_vehiculo.length === 0 ||
            !filters.ids_tipo_alerta ||
            filters.ids_tipo_alerta.length === 0) {
            return {
                component: <NoSelectionEmptyState reportType={tipoReporte} />,
                show: true
            };
        }

        return {
            component: <NoDataEmptyState reportType={tipoReporte} />,
            show: true
        };
    }, [filters, tipoReporte]);



    const generarFiltro = useCallback((filtro) => {
        const decoded = jwtDecode(localStorage.getItem('token'));
        return {
            fecha_inicio: filtro.fecha_inicio,
            fecha_fin: filtro.fecha_fin,
            ids_tipo_alerta: filtro.ids_tipo_alerta,
            uids_vehiculo: filtro.uids_vehiculo,
            empresaUid: decoded.uid_empresa,
            page: 1,
            uid_usuario: decoded.uid_usuario,
            perfil: 1,
        };
    }, []);

    const handleResetReport = (typeReport) => {

        // ✅ LIMPIAR ESTADOS DE SELECCIÓN
        setSearchInitiated(false);
        setSelectedVehicle(null);
        setHighlightedVehicle(null);

        // ✅ LIMPIAR FILTROS DE BÚSQUEDA SI LOS HAY
        setSearchValue('');

        // ✅ RESETEAR PÁGINA A LA PRIMERA
        setCurrentPage(1);

        // ✅ LIMPIAR CACHÉ DE PÁGINAS PARA FORZAR REGENERACIÓN
        setPagesCache({});

        // ✅ RESETEAR ESTADOS DE FILTRO DE ALERTA SI EXISTEdfsdfds
        setSelectedAlertType(null);
        setHighlightedAlertType(null);
        setTipoReporte(typeReport);
    }

    const handleGenerateReport = useCallback((selectedFilters) => {
        setMaxProgress(0);
        if (!selectedFilters.uids_vehiculo || selectedFilters.uids_vehiculo.length === 0) {
            setValidationError("Debe seleccionar al menos un vehículo para generar el reporte");
            return;
        }

        if (!selectedFilters.fecha_inicio || !selectedFilters.fecha_fin) {
            setValidationError("Debe seleccionar fechas válidas para el reporte");
            return;
        }

        const fechaInicio = dayjs(selectedFilters.fecha_inicio);
        const fechaFin = dayjs(selectedFilters.fecha_fin);
        const diferenciaDias = fechaFin.diff(fechaInicio, 'day');

        if (diferenciaDias > 31) {
            setValidationError('El rango de fechas no puede ser mayor a 31 días');
            return;
        }

        if (diferenciaDias < 0) {
            setValidationError('La fecha de inicio no puede ser posterior a la fecha de fin');
            return;
        }

        clearErrors();
        store.editarFiltrosAfter(null);
        store.editarFiltrosBefore(null);
        paginadorAlertas.editarDatos([]);
        paginadorAlertas.editarProgresoCargado(0);
        paginadorAlertas.editarError(null);

        paginadorAlertasAfter.editarDatos([]);
        paginadorAlertasAfter.editarProgresoCargado(0);
        paginadorAlertasAfter.editarError(null);
        paginadorAlertasBefore.editarDatos([]);
        paginadorAlertasBefore.editarProgresoCargado(0);
        paginadorAlertasBefore.editarError(null);

        setSearchInitiated(true);

        try {
            if (safetyTimeoutRef.current) {
                clearTimeout(safetyTimeoutRef.current);
                safetyTimeoutRef.current = null;
            }

            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
                monitorIntervalRef.current = null;
            }
            limpiarCache();

            paginadorAlertas.reiniciar();
            paginadorAlertasAfter.reiniciar();
            paginadorAlertasBefore.reiniciar();

            let debugDefault = {
                searchStartTime: null,
                searchEndTime: null,

                connections: {
                    'alertas-gps': {
                        name: 'Datos Actuales (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-after': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-before': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara': {
                        name: 'Datos Actuales (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-after': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-before': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones': {
                        name: 'Datos Actuales (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-after': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-before': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                },

                globalTotalRecordsExpected: 0,
                globalTotalRecordsReceived: 0,
                globalDownloadProgress: 0,
                activeConnections: 0,

                componentTimes: {
                    table: { start: null, end: null },
                    vehicles: { start: null, end: null },
                    alerts: { start: null, end: null },
                    map: { start: null, end: null },
                    frequency: { start: null, end: null }
                }
            };
            let debugDefaultFrequency = {
                searchStartTime: null,
                searchEndTime: null,

                connections: {
                    'alertas-gps': {
                        name: 'Datos Actuales (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-after': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-before': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara': {
                        name: 'Datos Actuales (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-after': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-before': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones': {
                        name: 'Datos Actuales (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-after': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-before': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                },

                globalTotalRecordsExpected: 0,
                globalTotalRecordsReceived: 0,
                globalDownloadProgress: 0,
                activeConnections: 0,

                componentTimes: {
                    'Periodo Actual': { start: null, end: null },
                    'Periodo Anterior': { start: null, end: null },
                }
            };
            setDebugMetrics(debugDefault);
            setDebugMetricsFrequency(debugDefaultFrequency);

            editarTotalCarga([]);
            setLoadingStates({ table: false, charts: false, maps: false });

            Object.keys(loadingComponents).forEach(component => {
                updateComponentProgress(component, 0, false);
            });

            setWorkerData({
                tableData: [],
                chartData: [],
                mapData: [],
                total: 0,
                totalPages: 0,
                chartDataGeneral: [],
                totalGeneral: 0,
                frecuenciaData: [],
                frecuenciaDataGeneral: [],
                vehiculoDataGeneral: [],
                frecuenciaHoraDataGeneral: [],
                frecuenciaDataAfter: [],
                frecuenciaHoraDataAfter: [],
                frecuenciaDataBefore: [],
                frecuenciaHoraDataBefore: [],
                isSingleDay: false,
                isSingleDayAfter: false,
                totalAfterGeneral: 0,
                isSingleDayBefore: false,
                totalBeforeGeneral: 0,
                filteredData: []
            });

            setCurrentPage(1);
            setClientSideTableData([]);
            setClientSideTotal(0);
            setClientSideTotalPages(1);
            setPagesCache({});
            setIsPageChanging(false);

            setSelectedVehicle(null);
            setSelectedAlertType(null);
            setHighlightedVehicle(null);
            setHighlightedAlertType(null);
            setSearchValue('');
            setIsExporting(false);
            setErrorMessage(null);

            alertColorsRef.current = new Map();
            usedColors.clear();

            setAlerts([]);
            setAlertsAfter([]);
            setAlertsBefore([]);

            setTimeout(() => {
                updateDebugMetrics({
                    searchStartTime: Date.now()
                });
                updateDebugMetricsFrequency({
                    searchStartTime: Date.now()
                });
                //console.log("⏱️ Tiempo de inicio registrado");
            }, 100);

            setFilters(selectedFilters);
            setShowFilters(false);

        } catch (error) {
            console.error("❌ Error durante la inicialización del reporte:", error);
            setApiError(`Error al inicializar reporte: ${error.message}`);
            setIsLoading(false);
            setSearchInitiated(false);
        }


        editarTotalCargaFrecuenciaAfter({
            ...prevDP,
        });
        editarTotalCargaFrecuenciaBefore({
            ...prevDP,
        });
    }, [
        clearErrors,
        limpiarCache,
        paginadorAlertas,
        paginadorAlertasAfter,
        paginadorAlertasBefore,
        loadingComponents,
        loadingComponentsFrequency,
        updateComponentProgress,
    ]);

    useEffect(() => {
        if (totalCargaFrecuenciaAfter.percent === 100 && totalCargaFrecuenciaBefore.percent === 100 && totalmapeoFrecuenciaAfter.percent === 100 && totalmapeoFrecuenciaBefore.percent === 100) {
            editarTotalCargaFrecuenciaAfter(prevDP);
            editarTotalCargaFrecuenciaBefore(prevDP);
            editarTotalMapeoFrecuenciaAfter(prevDP);
            editarTotalMapeoFrecuenciaBefore(prevDP);
        }
    }, [totalCargaFrecuenciaAfter, totalCargaFrecuenciaBefore, totalmapeoFrecuenciaAfter, totalmapeoFrecuenciaBefore]);


    const handleSelectVehicle = useCallback((vehicle) => {
        setSelectedVehicle(prevVehicle => prevVehicle === vehicle ? null : vehicle);
        setHighlightedVehicle(prevVehicle => prevVehicle === vehicle ? null : vehicle);
        setSelectedAlertType(null);
        setHighlightedAlertType(null);
    }, []);

    const handleSelectAlertType = useCallback((alertType) => {
        console.log('🎯 Seleccionando tipo de alerta:', alertType);

        // ✅ ACTUALIZAR ESTADOS DE SELECCIÓN
        setSelectedAlertType(alertType);
        setHighlightedAlertType(alertType);

        // ✅ RESETEAR PÁGINA A LA PRIMERA
        setCurrentPage(1);
        setPagesCache({});

        // ✅ FORZAR RECÁLCULO DE COMPONENTES AFECTADOS (tabla, mapa, frecuencia)
        // NO afectar vehicles ni alerts charts que deben mantener vista general
        setLoadingComponents(prev => ({
            ...prev,
            table: { loading: false, progress: 0, complete: false },
            map: { loading: false, progress: 0, complete: false },
            frequency: { loading: false, progress: 0, complete: false }
            // ✅ NO resetear vehicles y alerts para mantener vista general
        }));

        console.log('✅ Filtro de alerta aplicado:', alertType);
    }, []);


    const handleClearFilters = useCallback(() => {
        // setSelectedVehicle(null);
        // setSelectedAlertType(null);
        // setHighlightedVehicle(null);
        // setHighlightedAlertType(null);
        console.log('🔄 Removiendo filtro de vehículo y restaurando estado inicial');

        // ✅ LIMPIAR ESTADOS DE SELECCIÓN
        setSelectedVehicle(null);
        setHighlightedVehicle(null);

        // ✅ LIMPIAR FILTROS DE BÚSQUEDA SI LOS HAY
        setSearchValue('');

        // ✅ RESETEAR PÁGINA A LA PRIMERA
        setCurrentPage(1);

        // ✅ LIMPIAR CACHÉ DE PÁGINAS PARA FORZAR REGENERACIÓN
        setPagesCache({});

        // ✅ RESETEAR ESTADOS DE FILTRO DE ALERTA SI EXISTE
        setSelectedAlertType(null);
        setHighlightedAlertType(null);

        // ✅ OPCIONAL: RESETEAR COMPONENTES PARA REGENERAR TODO
        // Esto forzará que todos los gráficos vuelvan a procesar sin filtros
        setLoadingComponents(prev => ({
            table: { loading: false, progress: 0, complete: false },
            vehicles: { loading: false, progress: 0, complete: false },
            alerts: { loading: false, progress: 0, complete: false },
            map: { loading: false, progress: 0, complete: false },
            frequency: { loading: false, progress: 0, complete: false }
        }));

        console.log('✅ Filtros removidos, regresando al estado inicial');

    }, []);

    const handleFiltroDetonados = useCallback(() => {
        console.log('🔄 Removiendo filtro de tipo de alerta y restaurando estado inicial');

        // ✅ LIMPIAR ESTADOS DE SELECCIÓN DE ALERTA
        setSelectedAlertType(null);
        setHighlightedAlertType(null);

        // ✅ MANTENER FILTRO DE VEHÍCULO SI EXISTE (solo limpiar alerta)
        // setSelectedVehicle permanece igual

        // ✅ RESETEAR PÁGINA A LA PRIMERA
        setCurrentPage(1);
        setPagesCache({});

        // ✅ LIMPIAR BÚSQUEDA
        setSearchValue('');

        // ✅ FORZAR RECÁLCULO DE COMPONENTES AFECTADOS 
        setLoadingComponents(prev => ({
            ...prev,
            table: { loading: false, progress: 0, complete: false },
            map: { loading: false, progress: 0, complete: false },
            frequency: { loading: false, progress: 0, complete: false }
            // ✅ NO resetear vehicles y alerts para mantener vista general
        }));

        console.log('✅ Filtro de alerta removido, manteniendo otros filtros');
    }, []);

    const handlePageSizeChange = useCallback((newSize) => {
        setPerPage(newSize);
    }, []);

    // ============================================================================
    // EFECTOS
    // ============================================================================

    // Efecto de limpieza al desmontar
    useEffect(() => {
        return () => {
            console.log("🧹 Limpieza al desmontar componente");

            if (safetyTimeoutRef.current) {
                clearTimeout(safetyTimeoutRef.current);
                safetyTimeoutRef.current = null;
            }

            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
                monitorIntervalRef.current = null;
            }
            limpiarCache();
        };
    }, []);

    // Efecto para controlar el estado de carga
    useEffect(() => {
        if (!searchInitiated) {
            setIsLoading(false);
            return;
        }
        const isDataLoading = paginadorAlertas.cargando;// || paginadorAlertasAfter.cargando || paginadorAlertasBefore.cargando;
        const isWorkerLoading = loadingStates.table;
        if (isDataLoading || (isWorkerLoading && alerts.length === 0)) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [paginadorAlertas.cargando, loadingStates.table, alerts.length, searchInitiated]);//paginadorAlertasAfter.cargando, paginadorAlertasBefore.cargando

    //
    // useEffect(() => {
    //     console.log("Estado de paginadores:", {
    //         principal: {
    //             cargando: paginadorAlertas.cargando,
    //             datos: paginadorAlertas.datos?.length || 0
    //         },
    //         historico: {
    //             cargando: paginadorAlertasBefore.cargando,
    //             datos: paginadorAlertasBefore.datos?.length || 0
    //         }
    //     });
    // }, [paginadorAlertas.cargando, paginadorAlertasBefore.cargando]);



    useEffect(() => {
        // console.log('📋 PRIORIDAD 1: useEffect de TABLA ejecutándose');
        // console.log('📋 Estado actual:', {
        //     alertsLength: alerts.length,
        //     cargando: paginadorAlertas.cargando,
        //     tableComplete: loadingComponents.table.complete,
        //     selectedVehicle,
        //     selectedAlertType,
        //     searchValue
        // });

        if (alerts.length === 0 || paginadorAlertas.cargando) {
            //console.log('⚠️ Tabla: Esperando datos actuales...');
            return;
        }

        const isInitialLoad = !loadingComponents.table.complete;
        const isRefiltering = loadingComponents.table.complete && (selectedVehicle || selectedAlertType || searchValue !== undefined);



        if (!isInitialLoad && !isRefiltering) {
            console.log('✅ Tabla: Ya completada y sin filtros activos, saltando');
            return;
        }

        const processTableData = () => {
            if (isInitialLoad) {
                //console.log("📋 CARGA INICIAL: Procesando tabla por primera vez...");
                updateComponentProgress('table', 10, false);
            } else {
                //console.log("🔄 RE-FILTRADO: Aplicando filtros a datos existentes...");
            }

            try {

                let filteredData = null;

                // 🔍 DEBUG: Ver datos por tipo de reporte
                console.log(`🔍 [Alertas ${tipoReporte}] Procesando ${alerts.length} alertas`);
                if (alerts.length > 0) {
                    console.log(`📋 [Alertas ${tipoReporte}] Primer registro - keys:`, Object.keys(alerts[0]));
                    console.log(`📋 [Alertas ${tipoReporte}] Primer registro - data:`, alerts[0]);
                    console.log(`🔑 [Alertas ${tipoReporte}] Campo random:`, alerts[0]?.random, "| Campo codigoaleatorio:", alerts[0]?.codigoaleatorio);
                }

                switch (tipoReporte) {

                    case 'gps':
                        filteredData = [...(alerts.map(alert => {
                            let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                            let alerta = store.alertas.find(item => item.label.toLowerCase() === alert.alerta.toLowerCase());
                            let titulo = alerta?.alerta_grupo?.find(item => item.tipo_alerta === parseInt(alert.tipo_real))?.titulo;
                            let newAlert = {
                                ...alert,
                                no_economico: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                tipo_vehiculo: vehiculo.tipo_vehiculo,
                                color_vehiculo: vehiculo.color_vehiculo,
                                alerta_titulo: titulo ?? ''
                            };
                            if (showExtendedColumns) {
                                newAlert.random = alert.random ?? 'N/A';
                            }
                            return newAlert;
                        }))];
                        break;

                    case 'omisiones':
                        filteredData = [...(alerts.map(alert => {
                            let vehiculo = store.vehiculos.find(item => item.value === alert.id_vehiculo);
                            let newAlert = {
                                ...alert,
                                no_economico: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                tipo_vehiculo: vehiculo.tipo_vehiculo,
                                color_vehiculo: vehiculo.color_vehiculo,
                                alerta_titulo: alert.alerta_real ?? '',
                                alerta_omisiones: 'esOmisiones'//alert.extras_alertas_omisiones.mensaje
                            };
                            if (showExtendedColumns) {
                                newAlert.random = alert.random ?? 'N/A';
                            }
                            return newAlert;
                        }))];

                        // Calcular tiempo abierto para registros "cerrado"
                        filteredData.sort((a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf());
                        filteredData.forEach((item, idx) => {
                            const alertaLower = (item.alerta || '').toLowerCase();
                            if (alertaLower.includes('cerrad')) {
                                // Buscar hacia atrás el "abierto" más reciente de la misma unidad
                                let encontrado = null;
                                for (let i = idx - 1; i >= 0; i--) {
                                    if (filteredData[i].id_vehiculo === item.id_vehiculo
                                        && (filteredData[i].alerta || '').toLowerCase().includes('abiert')) {
                                        encontrado = filteredData[i];
                                        break;
                                    }
                                }
                                if (encontrado) {
                                    const diff = dayjs(item.fecha).diff(dayjs(encontrado.fecha), 'second');
                                    const h = Math.floor(diff / 3600);
                                    const m = Math.floor((diff % 3600) / 60);
                                    const s = diff % 60;
                                    item.tiempo_abierto_calc = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                                } else {
                                    item.tiempo_abierto_calc = '--';
                                }
                            } else {
                                item.tiempo_abierto_calc = '--';
                            }
                        });

                        break;

                    case 'camara':
                        filteredData = [...(alerts.map(alert => {
                            let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                            let newAlert = {
                                ...alert,
                                no_economico: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                tipo_vehiculo: vehiculo.tipo_vehiculo,
                                color_vehiculo: vehiculo.color_vehiculo,
                                alerta_titulo: alert.alerta ?? ''
                            };
                            if (showExtendedColumns) {
                                newAlert.random = alert.random ?? 'N/A';
                            }
                            return newAlert;
                        }))];
                        break;

                    default:
                        break;
                }

                // let filteredData = [...(alerts.map(alert => {
                //     let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                //     let newAlert = {
                //         ...alert,
                //         no_economico: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                //     };
                //     return newAlert;
                // }))];

                if (selectedVehicle) {
                    //console.log('🚗 Filtrando por vehículo:', selectedVehicle);
                    filteredData = filteredData.filter(alert =>
                        alert.no_economico === selectedVehicle.title
                    );
                    //console.log('📊 Resultados después de filtro vehicular:', filteredData.length);
                }

                if (selectedAlertType) {
                    //console.log('🚨 Filtrando por tipo de alerta:', selectedAlertType);
                    filteredData = filteredData.filter(alert =>
                        alert.alerta === selectedAlertType
                    );
                    //console.log('📊 Resultados después de filtro de alertas:', filteredData.length);
                }



                // Aplicar filtro de búsqueda solo si hay un valor de búsqueda
                if (searchValue && searchValue.trim()) {
                    console.log('🔍 Filtrando por búsqueda:', searchValue);
                    const searchLower = searchValue.toLowerCase().trim();

                    // Función para formatear fecha (similar a la del componente)
                    const formatFecha = (fechaStr) => {
                        if (!fechaStr || typeof fechaStr !== 'string') return '';
                        try {
                            // Detectar formato de fecha
                            if (fechaStr.includes('-')) {
                                // Formato: "2025-07-18 06:21:20"
                                const parts = fechaStr.split(' ');
                                if (parts.length >= 2) {
                                    const datePart = parts[0];
                                    const timePart = parts[1];
                                    const [year, month, day] = datePart.split('-');
                                    return `${day}/${month}/${year} ${timePart}`;
                                }
                            }
                            return fechaStr;
                        } catch (error) {
                            return fechaStr;
                        }
                    };

                    // Función de filtrado mejorada
                    const matchesSearch = (alert) => {
                        // Buscar en todos los valores del objeto
                        const basicMatch = Object.values(alert).some(value =>
                            value && value.toString().toLowerCase().includes(searchLower)
                        );

                        if (basicMatch) return true;

                        // Buscar específicamente en fechas formateadas
                        if (alert.fecha) {
                            const fechaFormateada = formatFecha(alert.fecha);
                            if (fechaFormateada.toLowerCase().includes(searchLower)) {
                                console.log('🔍 Match encontrado en fecha formateada:', {
                                    searchValue: searchLower,
                                    fechaOriginal: alert.fecha,
                                    fechaFormateada: fechaFormateada,
                                    vehiculo: alert.no_economico
                                });
                                return true;
                            }
                        }

                        // Buscar específicamente en marcadores
                        const marcadoresContent = [];

                        // Agregar tipos de marcadores disponibles
                        if (alert.cliente && alert.cliente.length > 0) {
                            marcadoresContent.push('Clientes');
                        }
                        if (alert.geoceca && (Array.isArray(alert.geoceca) ? alert.geoceca.length > 0 : alert.geoceca)) {
                            marcadoresContent.push('Geocercas');
                        }
                        if (alert.referencia && alert.referencia.length > 0) {
                            marcadoresContent.push('Referencias');
                        }

                        // Buscar en los tipos de marcadores
                        const marcadoresMatch = marcadoresContent.some(tipo =>
                            tipo.toLowerCase().includes(searchLower)
                        );

                        if (marcadoresMatch) {
                            console.log('🔍 Match encontrado en marcadores:', {
                                searchValue: searchLower,
                                marcadores: marcadoresContent,
                                vehiculo: alert.no_economico
                            });
                            return true;
                        }

                        return false;
                    };

                    filteredData = filteredData.filter(matchesSearch);
                    console.log('📊 Resultados filtrados por búsqueda:', filteredData.length);
                } else {
                    //console.log('🔍 No hay filtro de búsqueda, usando todos los datos:', filteredData.length);
                }

                if (isInitialLoad) {
                    updateComponentProgress('table', 70, false);
                }

                filteredData = showExtendedColumns
                    ? attachClosureDurations(filteredData)
                    : stripClosureDurations(filteredData);

                setWorkerData(prev => ({
                    ...prev,
                    filteredData,
                    tableData: filteredData,
                    total: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / perPage)
                }));

                if (isInitialLoad) {
                    updateComponentProgress('table', 100, true);
                    //console.log("✅ CARGA INICIAL: Tabla completada");
                } else {
                    //console.log("✅ RE-FILTRADO: Tabla actualizada con filtros");
                }

            } catch (error) {
                console.error("❌ Error procesando tabla:", error);
                if (isInitialLoad) {
                    updateComponentProgress('table', 100, true);
                }
            }
        };

        processTableData();

    }, [
        alerts.length,
        paginadorAlertas.cargando,
        selectedVehicle,
        selectedAlertType,
        searchValue,
        perPage,
        loadingComponents.table.complete,
        updateComponentProgress,
        showExtendedColumns
    ]);

    useEffect(() => {
        //console.log('🚗 useEffect de VEHÍCULOS ejecutándose con alertas:', alerts.length);

        if (alerts.length === 0 || paginadorAlertas.cargando) {
            //console.log('⚠️ Vehículos: No hay datos o aún cargando');
            return;
        }

        if (loadingComponents.vehicles.complete) {
            //console.log('✅ Vehículos ya procesados, saltando');
            return;
        }

        const processVehicleData = () => {
            //console.log("🚗 Procesando datos de vehículos...");

            try {
                updateComponentProgress('vehicles', 10, false);

                //Arreglo de agrupado por vehiculos 
                const vehiculoMap = {};

                //Tipo de reporte 
                switch (tipoReporte) {

                    case 'gps':
                        alerts.forEach(alert => {
                            if (!vehiculoMap[alert.no_economico]) {
                                let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                                vehiculoMap[alert.no_economico] = {
                                    id: alert.no_economico,
                                    no_economico: alert.no_economico,
                                    title: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                    totalAlerts: 0,
                                    color: getAlertColor(alert.no_economico),
                                    tipo_vehiculo: vehiculo.tipo_vehiculo,
                                    color_vehiculo: vehiculo.color_vehiculo,
                                };
                            }
                            vehiculoMap[alert.no_economico].totalAlerts += 1;
                        });
                        break;

                    case 'omisiones':
                        alerts.forEach(alert => {


                            if (!vehiculoMap[alert.no_economico]) {
                                let vehiculo = store.vehiculos.find(item => item.value === alert.id_vehiculo);
                                vehiculoMap[alert.no_economico] = {
                                    id: alert.no_economico,
                                    no_economico: alert.no_economico,
                                    title: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                    totalAlerts: 0,
                                    color: getAlertColor(alert.no_economico),
                                    tipo_vehiculo: vehiculo.tipo_vehiculo,
                                    color_vehiculo: vehiculo.color_vehiculo,
                                };
                            }
                            vehiculoMap[alert.no_economico].totalAlerts += 1;
                        });
                        break;

                    case 'camara':
                        alerts.forEach(alert => {
                            if (!vehiculoMap[alert.no_economico]) {
                                let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                                vehiculoMap[alert.no_economico] = {
                                    id: alert.no_economico,
                                    no_economico: alert.no_economico,
                                    title: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                                    totalAlerts: 0,
                                    color: getAlertColor(alert.no_economico),
                                    tipo_vehiculo: vehiculo.tipo_vehiculo,
                                    color_vehiculo: vehiculo.color_vehiculo,
                                };
                            }
                            vehiculoMap[alert.no_economico].totalAlerts += 1;
                        });
                        break;

                    default:
                        break;
                }

                // alerts.forEach(alert => {
                //     if (!vehiculoMap[alert.no_economico]) {
                //         let vehiculo = store.vehiculos.find(item => item.value === alert.uid_vehiculo);
                //         vehiculoMap[alert.no_economico] = {
                //             id: alert.no_economico,
                //             no_economico: alert.no_economico,
                //             title: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                //             totalAlerts: 0,
                //             color: getAlertColor(alert.no_economico)
                //         };
                //     }
                //     vehiculoMap[alert.no_economico].totalAlerts += 1;
                // });

                updateComponentProgress('vehicles', 60, false);

                const vehiculoDataGeneral = Object.values(vehiculoMap);
                vehiculoDataGeneral.forEach(vehicle => {
                    vehicle.progress = alerts.length > 0 ?
                        Number(((vehicle.totalAlerts / alerts.length) * 100).toFixed(1)) : 0;
                });
                vehiculoDataGeneral.sort((a, b) => b.totalAlerts - a.totalAlerts);

                updateComponentProgress('vehicles', 90, false);

                setWorkerData(prev => ({
                    ...prev,
                    vehiculoDataGeneral
                }));

                updateComponentProgress('vehicles', 100, true);
                //console.log("✅ Datos de vehículos procesados:", vehiculoDataGeneral.length, "vehículos");

            } catch (error) {
                console.error("❌ Error procesando vehículos:", error);
                updateComponentProgress('vehicles', 100, true);
            }
        };

        // Pequeño delay para no bloquear la tabla
        setTimeout(processVehicleData, 50)
        /*NOTA: PUSIERON getAlertColor EN EL EFFECT PARA ESCUCHAR CAMBIOS, PERO ES UN METODO Y SE LLAMA AQUI MISMO ASI QUE SE REMOVIO POR QUE HACIA CICLAR EL CODIGO*/
    }, [alerts.length, paginadorAlertas.cargando, loadingComponents.vehicles.complete, updateComponentProgress]);

    // ✅ EFECTO para ALERTAS
    useEffect(() => {
        //console.log('📊 useEffect de ALERTAS ejecutándose con alertas:', alerts.length, 'selectedVehicle:', selectedVehicle);

        if (alerts.length === 0 || paginadorAlertas.cargando) {
            //console.log('⚠️ Alertas: No hay datos o aún cargando');
            return;
        }

        // ✅ SEPARAR LÓGICA: Carga inicial vs Re-filtrado
        const isInitialLoad = !loadingComponents.alerts.complete;
        const isRefiltering = loadingComponents.alerts.complete && selectedVehicle;

        if (!isInitialLoad && !isRefiltering) {
            //console.log('✅ Alertas: Ya procesadas y sin filtros activos, saltando');
            return;
        }

        const processAlertData = () => {
            if (isInitialLoad) {
                //console.log("📊 CARGA INICIAL: Procesando datos de alertas...");
                updateComponentProgress('alerts', 10, false);
            } else {
                //console.log("🔄 RE-FILTRADO: Recalculando alertas para vehículo seleccionado...");      
            }

            try {
                // ✅ FILTRAR ALERTAS ANTES DE PROCESAR
                let alertsToProcess = alerts;
                if (selectedVehicle) {
                    //console.log('🔍 Filtrando alertas para vehículo:', selectedVehicle);
                    alertsToProcess = alerts.filter(alert => alert.no_economico === selectedVehicle.id);
                    //console.log('📊 Alertas filtradas para gráfico:', alertsToProcess.length);
                }

                if (isInitialLoad) {
                    updateComponentProgress('alerts', 30, false);
                }

                const chartMapGeneral = {};
                alertsToProcess.forEach(alert => {
                    if (!chartMapGeneral[alert.alerta]) {
                        chartMapGeneral[alert.alerta] = {
                            title: alert.alerta,
                            target: 0,
                            color: getAlertColor(alert.alerta)
                        };
                    }
                    chartMapGeneral[alert.alerta].target += 1;
                });

                if (isInitialLoad) {
                    updateComponentProgress('alerts', 60, false);
                }

                const chartDataGeneral = Object.values(chartMapGeneral);
                chartDataGeneral.forEach(a => {
                    a.amount = alertsToProcess.length > 0 ?
                        Number(((a.target / alertsToProcess.length) * 100).toFixed(1)) : 0;
                });
                chartDataGeneral.sort((a, b) => b.target - a.target);

                if (isInitialLoad) {
                    updateComponentProgress('alerts', 90, false);
                }

                setWorkerData(prev => ({
                    ...prev,
                    chartDataGeneral,
                    totalGeneral: alertsToProcess.length
                }));

                if (isInitialLoad) {
                    updateComponentProgress('alerts', 100, true);
                    //console.log("✅ CARGA INICIAL: Datos de alertas procesados:", chartDataGeneral.length, "tipos de alertas");
                } else {
                    //console.log("✅ RE-FILTRADO: Gráfico de alertas actualizado para vehículo:", selectedVehicle);
                }

            } catch (error) {
                console.error("❌ Error procesando alertas:", error);
                if (isInitialLoad) {
                    updateComponentProgress('alerts', 100, true);
                }
            }
        };

        setTimeout(processAlertData, isInitialLoad ? 100 : 50);
        /*NOTA: PUSIERON getAlertColor EN EL EFFECT PARA ESCUCHAR CAMBIOS, PERO ES UN METODO Y SE LLAMA AQUI MISMO ASI QUE SE REMOVIO POR QUE HACIA CICLAR EL CODIGO*/
    }, [alerts.length, selectedVehicle, paginadorAlertas.cargando, loadingComponents.alerts.complete, updateComponentProgress]);

    // ✅ EFECTO para MAPA
    useEffect(() => {
        //console.log('🗺️ useEffect de MAPA ejecutándose con alertas:', alerts.length, 'selectedVehicle:', selectedVehicle);

        if (alerts.length === 0 || paginadorAlertas.cargando) {
            //console.log('⚠️ Mapa: No hay datos o aún cargando');
            return;
        }

        // ✅ SEPARAR LÓGICA: Carga inicial vs Re-filtrado
        const isInitialLoad = !loadingComponents.map.complete;
        //const isRefiltering = loadingComponents.map.complete && selectedVehicle;
        const isRefiltering = loadingComponents.map.complete && (selectedVehicle || selectedAlertType || searchValue);

        if (!isInitialLoad && !isRefiltering) {
            //console.log('✅ Mapa: Ya procesado y sin filtros activos, saltando');
            return;
        }

        const processMapData = () => {
            if (isInitialLoad) {
                //console.log("🗺️ CARGA INICIAL: Procesando datos del mapa...");
                updateComponentProgress('map', 10, false);
            } else {
                console.log("🔄 RE-FILTRADO: Recalculando mapa para vehículo seleccionado...");
                // ✅ NO actualizar progreso durante re-filtrado para evitar ciclos
            }

            try {
                // ✅ FILTRAR ALERTAS ANTES DE PROCESAR
                let alertsToProcess = alerts;
                if (selectedVehicle) {
                    //console.log('🔍 Filtrando mapa para vehículo:', selectedVehicle);
                    alertsToProcess = alerts.filter(alert => alert.no_economico === selectedVehicle.id);
                    //console.log('📊 Alertas filtradas para mapa:', alertsToProcess.length);
                }

                if (selectedAlertType) {
                    //console.log('🔍 Filtrando mapa para tipo de alerta:', selectedAlertType);
                    alertsToProcess = alertsToProcess.filter(alert => alert.alerta === selectedAlertType);
                }

                if (isInitialLoad) {
                    updateComponentProgress('map', 40, false);
                }

                const mapData = alertsToProcess.filter(item =>
                    typeof item.latitud === 'number' &&
                    typeof item.longitud === 'number' &&
                    !isNaN(item.latitud) &&
                    !isNaN(item.longitud)
                );

                if (isInitialLoad) {
                    updateComponentProgress('map', 80, false);
                }

                setWorkerData(prev => ({
                    ...prev,
                    mapData
                }));

                if (isInitialLoad) {
                    updateComponentProgress('map', 100, true);
                    //console.log("✅ CARGA INICIAL: Datos del mapa procesados:", mapData.length, "puntos válidos");
                } else {
                    //console.log("✅ RE-FILTRADO: Mapa actualizado para vehículo:", selectedVehicle, "con", mapData.length, "puntos");
                }

            } catch (error) {
                console.error("❌ Error procesando mapa:", error);
                if (isInitialLoad) {
                    updateComponentProgress('map', 100, true);
                }
            }
        };

        setTimeout(processMapData, isInitialLoad ? 150 : 50);

    }, [alerts.length, selectedVehicle,
        selectedAlertType,
    paginadorAlertas.cargando,
    loadingComponents.map.complete, updateComponentProgress]);


    const frequencyResetRef = useRef(false);
    const frequencyResetRefAfter = useRef(false);
    const frequencyResetRefBefore = useRef(false);

    useEffect(() => {
        if (paginadorAlertasBefore.datos && paginadorAlertasBefore.datos.length > 0) {
            console.log('📊 Sincronizando alertsBefore desde paginador:', paginadorAlertasBefore.datos.length, 'registros');
            setAlertsBefore(paginadorAlertasBefore.datos);

            if (!frequencyResetRefBefore.current && loadingComponents.frequency.complete) {
                console.log('🔄 Reseteando frecuencia para reprocesamiento con datos históricos');
                frequencyResetRefBefore.current = true;

                setTimeout(() => {
                    setLoadingComponents(prev => ({
                        ...prev,
                        frequency: { loading: false, progress: 0, complete: false }
                    }));
                }, 0);

                setTimeout(() => {
                    console.log('✅ Liberando reset de frecuencia');
                    frequencyResetRefBefore.current = false;
                }, 200);
            }
        }
    }, [paginadorAlertasBefore.datos?.length]);

    useEffect(() => {
        if (paginadorAlertasAfter.datos && paginadorAlertasAfter.datos.length > 0) {
            console.log('📊 Sincronizando alertsAfter desde paginador:', paginadorAlertasAfter.datos.length, 'registros');
            setAlertsAfter(paginadorAlertasAfter.datos);

            if (!frequencyResetRefAfter.current && loadingComponents.frequency.complete) {
                console.log('🔄 Reseteando frecuencia para reprocesamiento con datos históricos');
                frequencyResetRefAfter.current = true;

                setTimeout(() => {
                    setLoadingComponents(prev => ({
                        ...prev,
                        frequency: { loading: false, progress: 0, complete: false }
                    }));
                }, 0);

                setTimeout(() => {
                    console.log('✅ Liberando reset de frecuencia');
                    frequencyResetRefAfter.current = false;
                }, 200);
            }
        }
    }, [paginadorAlertasAfter.datos?.length]);

    useEffect(() => {
        //console.log('📈 useEffect de FRECUENCIA ejecutándose con alertas:', paginadorAlertas.datos?.length || 0, 'selectedVehicle:', selectedVehicle);

        // ✅ CONDICIONES SIMPLIFICADAS
        const hasMainData = paginadorAlertas.datos?.length > 0;
        const isLoadingMain = paginadorAlertas.cargando;
        const isFrequencyComplete = loadingComponents.frequency.complete;
        const isResetPending = frequencyResetRef.current;

        // ✅ SEPARAR LÓGICA: Carga inicial vs Re-filtrado
        const isInitialLoad = !isFrequencyComplete;
        const isRefiltering = isFrequencyComplete && (selectedVehicle || selectedAlertType || searchValue);

        //console.log('📈 Estado de frecuencia:', {
        //    hasMainData,
        //    isLoadingMain,
        //    isFrequencyComplete,
        //    isResetPending,
        //    isInitialLoad,
        //    isRefiltering
        //});

        // ✅ BLOQUEAR CORRECTAMENTE
        if (!hasMainData || isLoadingMain) {
            //if (!hasMainData) console.log('⚠️ Frecuencia: No hay datos principales');
            //if (isLoadingMain) console.log('⚠️ Frecuencia: Datos principales aún cargando');
            return;
        }

        // ✅ BLOQUEAR SI YA ESTÁ COMPLETO Y NO HAY RESET NI FILTRADO
        if (isFrequencyComplete && !isResetPending && !isRefiltering) {
            //console.log('⚠️ Frecuencia: Ya completado y sin cambios pendientes');
            return;
        }

        // ✅ LIBERAR EL RESET SI ESTABA PENDIENTE
        if (isResetPending) {
            console.log('🔄 Procesando frecuencia después del reset');
            frequencyResetRef.current = false;
        }

        const processFrequencyData = () => {
            if (isInitialLoad) {
                //console.log("📈 CARGA INICIAL: Procesando datos de frecuencia por primera vez...");
                updateComponentProgress('frequency', 10, false);
            } else if (isRefiltering) {
                console.log("🔄 RE-FILTRADO: Recalculando frecuencia para vehículo seleccionado...");
            }

            try {
                // ✅ FILTRAR DATOS ANTES DE PROCESAR
                let alertsActuales = paginadorAlertas.datos.map(agregarAlertaTitulo).map(alert => {
                    if (filters.tipo_reporte === 'omisiones') {
                        return { ...alert, fecha: dayjs.utc(alert.fecha_hora || alert.fecha).local() };
                    } else {
                        return { ...alert, fecha: dayjs(alert.fecha_hora || alert.fecha) };
                    }
                }) || [];

                const totalOriginalActual = alertsActuales.length;

                if (selectedAlertType) {
                    alertsActuales = alertsActuales.filter(alert => alert.alerta === selectedAlertType);
                }

                if (selectedVehicle) {
                    alertsActuales = alertsActuales.filter(alert => alert.no_economico === selectedVehicle.id);
                }

                if (selectedAlertType) {
                    alertsActuales = alertsActuales.filter(alert => alert.alerta === selectedAlertType);
                }

                if (searchValue && searchValue.trim() !== "") {
                    const searchLower = searchValue.toLowerCase();
                    alertsActuales = alertsActuales.filter(alert =>
                        Object.values(alert).some(val =>
                            String(val).toLowerCase().includes(searchLower)
                        )
                    );
                }

                // ✅ CALCULAR TOTALES FILTRADOS
                const totalFiltradoActual = alertsActuales.length;

                const mode = 'SOLO_ACTUAL';

                //console.log('📈 Modo de frecuencia:', mode, {
                //    vehiculo_filtrado: selectedVehicle || 'TODOS'
                //});

                if (isInitialLoad) {
                    updateComponentProgress('frequency', 30, false);
                }

                let frecuenciaDataGeneral = [];
                let frecuenciaHoraDataGeneral = [];
                let isSingleDay = false;

                const fechasUnicasActuales = [...new Set(
                    alertsActuales.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                )].sort();

                isSingleDay = fechasUnicasActuales.length === 1;

                if (isSingleDay) {
                    const frecuenciaHoraMapGeneral = {};
                    alertsActuales.forEach(alert => {
                        let fechaParaProcesar;

                        if (filters.tipo_reporte === 'omisiones') {
                            fechaParaProcesar = dayjs.utc(alert.fecha).local();
                        } else {
                            fechaParaProcesar = dayjs(alert.fecha_hora || alert.fecha);
                        }

                        const hora = fechaParaProcesar.format('HH:00');
                        if (!frecuenciaHoraMapGeneral[hora]) {
                            frecuenciaHoraMapGeneral[hora] = {
                                hora: hora,
                                fecha: hora,
                                cantidad: 0
                            };
                        }
                        frecuenciaHoraMapGeneral[hora].cantidad++;
                    });

                    frecuenciaHoraDataGeneral = Object.values(frecuenciaHoraMapGeneral)
                        .sort((a, b) => a.hora.localeCompare(b.hora));

                } else {
                    const fechaInicio = dayjs(fechasUnicasActuales[0]);
                    const fechaFin = dayjs(fechasUnicasActuales[fechasUnicasActuales.length - 1]);
                    const diasTotales = fechaFin.diff(fechaInicio, 'day') + 1;

                    if (diasTotales <= 0) {
                        console.error('❌ Error: Rango de fechas inválido');
                        if (isInitialLoad) {
                            updateComponentProgress('frequency', 100, true);
                        }
                        return;
                    }

                    const frecuenciaMapGeneral = {};

                    for (let i = 0; i < diasTotales; i++) {
                        const fecha = fechaInicio.add(i, 'day');
                        const fechaStr = fecha.format('YYYY-MM-DD');
                        const diaNumero = fecha.format('DD');

                        frecuenciaMapGeneral[fechaStr] = {
                            fecha: diaNumero,
                            fecha_completa: fechaStr,
                            cantidad: 0
                        };
                    }

                    alertsActuales.forEach(alert => {
                        const fechaAlert = dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD');
                        if (frecuenciaMapGeneral[fechaAlert]) {
                            frecuenciaMapGeneral[fechaAlert].cantidad++;
                        }
                    });

                    frecuenciaDataGeneral = Object.values(frecuenciaMapGeneral)
                        .sort((a, b) => a.fecha_completa.localeCompare(b.fecha_completa));

                    if (isInitialLoad) {
                        updateComponentProgress('frequency', 0, false);
                    }

                }

                if (isInitialLoad) {
                    updateComponentProgress('frequency', 80, false);
                }
                setWorkerData(prev => ({
                    ...prev,
                    frecuenciaDataGeneral,
                    frecuenciaHoraDataGeneral,
                    isSingleDay,
                    totalCurrentFiltered: (selectedVehicle || selectedAlertType || searchValue) ? totalFiltradoActual : totalOriginalActual,
                    frequencyMode: mode
                }));

                if (isInitialLoad) {
                    updateComponentProgress('frequency', 100, true);
                    //console.log("✅ CARGA INICIAL: Datos de frecuencia procesados en modo:", mode);
                } else if (isRefiltering) {
                    console.log("✅ RE-FILTRADO: Frecuencia actualizada para vehículo:", selectedVehicle);
                }

            } catch (error) {
                console.error("❌ Error al procesar datos de frecuencia:", error);
                if (isInitialLoad) {
                    updateComponentProgress('frequency', 100, true);
                }
            }
        };

        processFrequencyData();

    }, [
        paginadorAlertas.datos?.length,
        paginadorAlertas.cargando,
        selectedVehicle,
        selectedAlertType,
        searchValue,
        loadingComponents.frequency.complete,
        filters.tipo_reporte,
        updateComponentProgress
    ]);


    useEffect(() => {
        //console.log('📈 useEffect de FRECUENCIA ejecutándose con alertas:', paginadorAlertas.datos?.length || 0, 'selectedVehicle:', selectedVehicle);

        // ✅ CONDICIONES SIMPLIFICADAS
        const hasMainData = paginadorAlertasAfter.datos?.length > 0;
        const isLoadingMain = paginadorAlertasAfter.cargando;
        const isFrequencyComplete = loadingComponentsFrequency['Periodo Actual'].complete;
        const isResetPending = frequencyResetRefAfter.current;

        // ✅ SEPARAR LÓGICA: Carga inicial vs Re-filtrado
        const isInitialLoad = !isFrequencyComplete;
        const isRefiltering = isFrequencyComplete && (selectedVehicle || selectedAlertType || searchValue);

        //console.log('📈 Estado de frecuencia:', {
        //    hasMainData,
        //    isLoadingMain,
        //    isFrequencyComplete,
        //    isResetPending,
        //    isInitialLoad,
        //    isRefiltering
        //});

        // ✅ BLOQUEAR CORRECTAMENTE
        if (!hasMainData || isLoadingMain) {
            //if (!hasMainData) console.log('⚠️ Frecuencia: No hay datos principales');
            //if (isLoadingMain) console.log('⚠️ Frecuencia: Datos principales aún cargando');
            return;
        }

        // ✅ BLOQUEAR SI YA ESTÁ COMPLETO Y NO HAY RESET NI FILTRADO
        if (isFrequencyComplete && !isResetPending && !isRefiltering) {
            //console.log('⚠️ Frecuencia: Ya completado y sin cambios pendientes');
            return;
        }

        // ✅ LIBERAR EL RESET SI ESTABA PENDIENTE
        if (isResetPending) {
            console.log('🔄 Procesando frecuencia después del reset');
            frequencyResetRefAfter.current = false;
        }

        const processFrequencyData = () => {
            if (isInitialLoad) {
                //console.log("📈 CARGA INICIAL: Procesando datos de frecuencia por primera vez...");
                updateComponentFrequencyProgress('Periodo Actual', 10, false);
                editarTotalMapeoFrecuenciaAfter({ ...prevDP, title: 'Generando periodo actual', percent: 10, color: "#2c7be5" });
            } else if (isRefiltering) {
                console.log("🔄 RE-FILTRADO: Recalculando frecuencia para vehículo seleccionado...");
            }

            try {
                // ✅ FILTRAR DATOS ANTES DE PROCESAR
                let alertsPrevias = paginadorAlertasAfter.datos.map(agregarAlertaTitulo).map(alert => {
                    if (filters.tipo_reporte === 'omisiones') {
                        return { ...alert, fecha: dayjs.utc(alert.fecha_hora || alert.fecha).local() };
                    } else {
                        return { ...alert, fecha: dayjs(alert.fecha_hora || alert.fecha) };
                    }
                }) || [];

                const totalOriginalPrevia = alertsPrevias.length;

                if (selectedAlertType) {
                    alertsPrevias = alertsPrevias.filter(alert => alert.alerta === selectedAlertType);
                }

                if (selectedVehicle) {
                    alertsPrevias = alertsPrevias.filter(alert => alert.no_economico === selectedVehicle.id);
                }

                if (selectedAlertType) {
                    alertsPrevias = alertsPrevias.filter(alert => alert.alerta === selectedAlertType);
                }

                if (searchValue && searchValue.trim() !== "") {
                    const searchLower = searchValue.toLowerCase();
                    alertsPrevias = alertsPrevias.filter(alert =>
                        Object.values(alert).some(val =>
                            String(val).toLowerCase().includes(searchLower)
                        )
                    );
                }

                // ✅ CALCULAR TOTALES FILTRADOS
                const totalFiltradoPrevias = alertsPrevias.length;

                // ✅ DETERMINAR MODO DE PROCESAMIENTO
                const hasHistoricalData = alertsPrevias.length > 0;
                const isLoadingHistorical = (paginadorAlertasAfter.cargando);

                const mode = hasHistoricalData ? 'COMPARATIVO' :
                    isLoadingHistorical ? 'SOLO_ACTUAL_TEMPORAL' : 'SOLO_ACTUAL';

                console.log('📈 Modo de frecuencia:', mode, {
                    datos_historicos: hasHistoricalData,
                    cargando_historicos: isLoadingHistorical,
                    vehiculo_filtrado: selectedVehicle || 'TODOS'
                });

                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Actual', 30, false);
                    editarTotalMapeoFrecuenciaAfter({ ...prevDP, title: 'Generando periodo actual', percent: 30, color: "#2c7be5" });
                }
                let frecuenciaDataAfter = [];
                let frecuenciaHoraDataAfter = [];
                let isSingleDayAfter = false;

                const fechasUnicasAfter = [...new Set(
                    alertsPrevias.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                )].sort();
                isSingleDayAfter = fechasUnicasAfter.length === 1

                if (isSingleDayAfter) {
                    console.log("✅ CARGA AFTER:", isSingleDayAfter);
                    if (hasHistoricalData) {
                        const frecuenciaHoraMapAfter = {};
                        alertsPrevias.forEach(alert => {
                            let fechaParaProcesar;

                            if (filters.tipo_reporte === 'omisiones') {
                                fechaParaProcesar = dayjs(alert.fecha);
                            } else {
                                fechaParaProcesar = dayjs(alert.fecha_hora || alert.fecha);
                            }

                            const hora = fechaParaProcesar.format('HH:00');
                            if (!frecuenciaHoraMapAfter[hora]) {
                                frecuenciaHoraMapAfter[hora] = {
                                    hora: hora,
                                    fecha: hora,
                                    cantidad: 0
                                };
                            }
                            frecuenciaHoraMapAfter[hora].cantidad++;
                        });

                        frecuenciaHoraDataAfter = Object.values(frecuenciaHoraMapAfter)
                            .sort((a, b) => a.hora.localeCompare(b.hora));

                        isSingleDayAfter = [...new Set(
                            alertsPrevias.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                        )].length === 1;
                    }

                } else {
                    if (hasHistoricalData) {
                        const fechaInicio = dayjs(fechasUnicasAfter[0]);
                        const fechaFin = dayjs(fechasUnicasAfter[fechasUnicasAfter.length - 1]);
                        const diasTotales = fechaFin.diff(fechaInicio, 'day') + 1;
                        console.log("✅ CARGA AFTER:", alertsPrevias.length);
                        const fechasHistoricas = [...new Set(
                            alertsPrevias.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                        )].sort();

                        if (fechasHistoricas.length > 0) {
                            const fechaInicioHistorica = dayjs(fechasHistoricas[0]);
                            const frecuenciaMapAfter = {};

                            for (let i = 0; i < diasTotales; i++) {
                                const fechaActual = fechaInicio.add(i, 'day');
                                const fechaAnterior = fechaInicioHistorica.add(i, 'day');
                                const diaNumero = fechaActual.format('DD');
                                const fechaAnteriorStr = fechaAnterior.format('YYYY-MM-DD');

                                frecuenciaMapAfter[diaNumero] = {
                                    fecha: diaNumero,
                                    fecha_historica: fechaAnteriorStr,
                                    cantidad: 0
                                };
                            }

                            alertsPrevias.forEach(alert => {
                                const fechaAlert = dayjs(alert.fecha_hora || alert.fecha);
                                const diasDesdeFechaInicioHistorica = fechaAlert.diff(fechaInicioHistorica, 'day');

                                if (diasDesdeFechaInicioHistorica >= 0 && diasDesdeFechaInicioHistorica < diasTotales) {
                                    const fechaEquivalenteActual = fechaInicio.add(diasDesdeFechaInicioHistorica, 'day');
                                    const diaNumero = fechaEquivalenteActual.format('DD');

                                    if (frecuenciaMapAfter[diaNumero]) {
                                        frecuenciaMapAfter[diaNumero].cantidad++;
                                    }
                                }
                            });

                            frecuenciaDataAfter = Object.values(frecuenciaMapAfter)
                                .sort((a, b) => parseInt(a.fecha) - parseInt(b.fecha));
                        }
                    }
                }

                if (isInitialLoad) {
                    editarTotalMapeoFrecuenciaAfter({ ...prevDP, title: 'Generando periodo actual', percent: 80, color: "#2c7be5" });
                    updateComponentFrequencyProgress('Periodo Actual', 80, false);
                }
                setWorkerData(prev => ({
                    ...prev,
                    frecuenciaDataAfter,
                    frecuenciaHoraDataAfter,
                    isSingleDayAfter,
                    totalAfterGeneral: (selectedVehicle || selectedAlertType || searchValue) ? totalFiltradoPrevias : totalOriginalPrevia,
                    frequencyMode: mode
                }));

                if (isInitialLoad) {
                    editarTotalMapeoFrecuenciaAfter({ ...prevDP, title: 'Generando periodo actual', percent: 100, color: "#2c7be5" });
                    updateComponentFrequencyProgress('Periodo Actual', 100, true);
                    //console.log("✅ CARGA INICIAL: Datos de frecuencia procesados en modo:", mode);
                } else if (isRefiltering) {
                    console.log("✅ RE-FILTRADO: Frecuencia actualizada para vehículo:", selectedVehicle);
                }

            } catch (error) {
                console.error("❌ Error al procesar datos de frecuencia:", error);
                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Actual', 100, false);
                    editarTotalMapeoFrecuenciaAfter({ ...prevDP });
                }
            }
        };

        processFrequencyData();

    }, [
        paginadorAlertasAfter.datos?.length,
        paginadorAlertasAfter.cargando,
        selectedVehicle,
        selectedAlertType,
        searchValue,
        updateComponentFrequencyProgress
    ]);



    useEffect(() => {
        //console.log('📈 useEffect de FRECUENCIA ejecutándose con alertas:', paginadorAlertasBefore.datos?.length || 0, 'selectedVehicle:', selectedVehicle);

        // ✅ CONDICIONES SIMPLIFICADAS
        const hasMainData = paginadorAlertasBefore.datos?.length > 0;
        const isLoadingMain = paginadorAlertasBefore.cargando;
        const isFrequencyComplete = loadingComponentsFrequency['Periodo Anterior'].complete;
        const isResetPending = frequencyResetRefBefore.current;

        // ✅ SEPARAR LÓGICA: Carga inicial vs Re-filtrado
        const isInitialLoad = !isFrequencyComplete;
        const isRefiltering = isFrequencyComplete && (selectedVehicle || selectedAlertType || searchValue);

        //console.log('📈 Estado de frecuencia:', {
        //    hasMainData,
        //    isLoadingMain,
        //    isFrequencyComplete,
        //    isResetPending,
        //    isInitialLoad,
        //    isRefiltering
        //});

        // ✅ BLOQUEAR CORRECTAMENTE
        if (!hasMainData || isLoadingMain) {
            //if (!hasMainData) console.log('⚠️ Frecuencia: No hay datos principales');
            //if (isLoadingMain) console.log('⚠️ Frecuencia: Datos principales aún cargando');
            return;
        }

        // ✅ BLOQUEAR SI YA ESTÁ COMPLETO Y NO HAY RESET NI FILTRADO
        if (isFrequencyComplete && !isResetPending && !isRefiltering) {
            //console.log('⚠️ Frecuencia: Ya completado y sin cambios pendientes');
            return;
        }

        // ✅ LIBERAR EL RESET SI ESTABA PENDIENTE
        if (isResetPending) {
            console.log('🔄 Procesando frecuencia después del reset');
            frequencyResetRefBefore.current = false;
        }
        const processFrequencyData = () => {
            if (isInitialLoad) {
                //console.log("📈 CARGA INICIAL: Procesando datos de frecuencia por primera vez...");
                updateComponentFrequencyProgress('Periodo Anterior', 10, false);
                editarTotalMapeoFrecuenciaBefore({ ...prevDP, title: 'Generando periodo anterior', percent: 10, color: "#f5803e" });
            } else if (isRefiltering) {
                console.log("🔄 RE-FILTRADO: Recalculando frecuencia para vehículo seleccionado...");
            }

            try {
                // ✅ FILTRAR DATOS ANTES DE PROCESAR
                let alertsAnteriores = paginadorAlertasBefore.datos.map(agregarAlertaTitulo).map(alert => {
                    if (filters.tipo_reporte === 'omisiones') {
                        return { ...alert, fecha: dayjs.utc(alert.fecha_hora || alert.fecha).local() };
                    } else {
                        return { ...alert, fecha: dayjs(alert.fecha_hora || alert.fecha) };
                    }
                }) || [];

                const totalOriginalAnterior = alertsAnteriores.length;

                if (selectedAlertType) {
                    alertsAnteriores = alertsAnteriores.filter(alert => alert.alerta === selectedAlertType);
                }

                if (selectedVehicle) {
                    alertsAnteriores = alertsAnteriores.filter(alert => alert.no_economico === selectedVehicle.id);
                }

                if (selectedAlertType) {
                    alertsAnteriores = alertsAnteriores.filter(alert => alert.alerta === selectedAlertType);
                }

                if (searchValue && searchValue.trim() !== "") {
                    const searchLower = searchValue.toLowerCase();
                    alertsAnteriores = alertsAnteriores.filter(alert =>
                        Object.values(alert).some(val =>
                            String(val).toLowerCase().includes(searchLower)
                        )
                    );
                }

                // ✅ CALCULAR TOTALES FILTRADOS
                const totalFiltradoAnterior = alertsAnteriores.length;

                // ✅ DETERMINAR MODO DE PROCESAMIENTO
                const hasHistoricalData = alertsAnteriores.length > 0;
                const isLoadingHistorical = (paginadorAlertasBefore.cargando);

                const mode = hasHistoricalData ? 'COMPARATIVO' :
                    isLoadingHistorical ? 'SOLO_ACTUAL_TEMPORAL' : 'SOLO_ACTUAL';

                console.log('📈 Modo de frecuencia:', mode, {
                    datos_historicos: hasHistoricalData,
                    cargando_historicos: isLoadingHistorical,
                    vehiculo_filtrado: selectedVehicle || 'TODOS'
                });

                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Anterior', 30, false);
                    editarTotalMapeoFrecuenciaBefore({ ...prevDP, title: 'Generando periodo anterior', percent: 30, color: "#f5803e" });
                }
                let frecuenciaDataBefore = [];
                let frecuenciaHoraDataBefore = [];
                let isSingleDayBefore = false;
                const fechasUnicasBefore = [...new Set(
                    alertsAnteriores.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                )].sort();

                isSingleDayBefore = fechasUnicasBefore.length === 1;

                if (isSingleDayBefore) {
                    if (hasHistoricalData) {
                        const frecuenciaHoraMapBefore = {};
                        alertsAnteriores.forEach(alert => {
                            let fechaParaProcesar;

                            if (filters.tipo_reporte === 'omisiones') {
                                fechaParaProcesar = dayjs.utc(alert.fecha).local();
                            } else {
                                fechaParaProcesar = dayjs(alert.fecha_hora || alert.fecha);
                            }

                            const hora = fechaParaProcesar.format('HH:00');
                            if (!frecuenciaHoraMapBefore[hora]) {
                                frecuenciaHoraMapBefore[hora] = {
                                    hora: hora,
                                    fecha: hora,
                                    cantidad: 0
                                };
                            }
                            frecuenciaHoraMapBefore[hora].cantidad++;
                        });

                        frecuenciaHoraDataBefore = Object.values(frecuenciaHoraMapBefore)
                            .sort((a, b) => a.hora.localeCompare(b.hora));

                        isSingleDayBefore = [...new Set(
                            alertsAnteriores.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                        )].length === 1;
                    }
                } else {
                    if (hasHistoricalData) {
                        const fechaInicio = dayjs(fechasUnicasBefore[0]);
                        const fechaFin = dayjs(fechasUnicasBefore[fechasUnicasBefore.length - 1]);
                        const diasTotales = fechaFin.diff(fechaInicio, 'day') + 1;
                        console.log("✅ CARGA AFTER:", alertsAnteriores.length);
                        const fechasHistoricas = [...new Set(
                            alertsAnteriores.map(alert => dayjs(alert.fecha_hora || alert.fecha).format('YYYY-MM-DD'))
                        )].sort();

                        if (fechasHistoricas.length > 0) {
                            const fechaInicioHistorica = dayjs(fechasHistoricas[0]);
                            const frecuenciaMapBefore = {};

                            for (let i = 0; i < diasTotales; i++) {
                                const fechaActual = fechaInicio.add(i, 'day');
                                const fechaAnterior = fechaInicioHistorica.add(i, 'day');
                                const diaNumero = fechaActual.format('DD');
                                const fechaAnteriorStr = fechaAnterior.format('YYYY-MM-DD');

                                frecuenciaMapBefore[diaNumero] = {
                                    fecha: diaNumero,
                                    fecha_historica: fechaAnteriorStr,
                                    cantidad: 0
                                };
                            }

                            alertsAnteriores.forEach(alert => {
                                const fechaAlert = dayjs(alert.fecha_hora || alert.fecha);
                                const diasDesdeFechaInicioHistorica = fechaAlert.diff(fechaInicioHistorica, 'day');

                                if (diasDesdeFechaInicioHistorica >= 0 && diasDesdeFechaInicioHistorica < diasTotales) {
                                    const fechaEquivalenteActual = fechaInicio.add(diasDesdeFechaInicioHistorica, 'day');
                                    const diaNumero = fechaEquivalenteActual.format('DD');

                                    if (frecuenciaMapBefore[diaNumero]) {
                                        frecuenciaMapBefore[diaNumero].cantidad++;
                                    }
                                }
                            });

                            frecuenciaDataBefore = Object.values(frecuenciaMapBefore)
                                .sort((a, b) => parseInt(a.fecha) - parseInt(b.fecha));
                        }
                    }
                }

                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Anterior', 80, false);
                    editarTotalMapeoFrecuenciaBefore({ ...prevDP, title: 'Generando periodo anterior', percent: 80, color: "#f5803e" });
                }
                setWorkerData(prev => ({
                    ...prev,
                    frecuenciaDataBefore,
                    frecuenciaHoraDataBefore,
                    isSingleDayBefore,
                    totalBeforeGeneral: (selectedVehicle || selectedAlertType || searchValue) ? totalFiltradoAnterior : totalOriginalAnterior,
                    frequencyMode: mode
                }));

                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Anterior', 100, true);
                    editarTotalMapeoFrecuenciaBefore({ ...prevDP, percent: 100, color: "#f5803e" });
                    //console.log("✅ CARGA INICIAL: Datos de frecuencia procesados en modo:", mode);
                } else if (isRefiltering) {
                    console.log("✅ RE-FILTRADO: Frecuencia actualizada para vehículo:", selectedVehicle);
                }

            } catch (error) {
                console.error("❌ Error al procesar datos de frecuencia:", error);
                if (isInitialLoad) {
                    updateComponentFrequencyProgress('Periodo Anterior', 100, false);
                    editarTotalMapeoFrecuenciaBefore({ ...prevDP });
                }
            }
        };

        processFrequencyData();

    }, [
        paginadorAlertasBefore.datos?.length,
        paginadorAlertasBefore.cargando,
        selectedVehicle,
        selectedAlertType,
        searchValue,
        updateComponentFrequencyProgress
    ]);

    // EFECTO PARA RESETEAR EL REF CUANDO CAMBIAN LOS FILTROS
    useEffect(() => {
        frequencyResetRef.current = false;
    }, [filters]);

    useEffect(() => {
        const allComplete = Object.values(loadingComponents).every(c => c.complete);
        if (allComplete && alerts.length > 0) {
            //console.log("🎉 Todos los componentes completados exitosamente");
        }
    }, [loadingComponents, alerts.length]);

    // Efecto para manejo de paginación client-side
    useEffect(() => {
        if (!workerData || !workerData.filteredData || workerData.filteredData.length === 0) return;

        const totalItems = workerData.filteredData.length;
        const totalPages = Math.ceil(totalItems / perPage);

        if (totalItems !== clientSideTotal) setClientSideTotal(totalItems);
        if (totalPages !== clientSideTotalPages) setClientSideTotalPages(totalPages);

        const cacheKey = `page_${currentPage}_size_${perPage}`;

        if (pagesCache[cacheKey]) {
            setClientSideTableData(pagesCache[cacheKey]);
            return;
        }

        const start = (currentPage - 1) * perPage;
        const end = Math.min(start + perPage, totalItems);
        const pageData = workerData.filteredData.slice(start, end);

        setPagesCache(prevCache => {
            const newCache = { ...prevCache, [cacheKey]: pageData };

            const cacheKeys = Object.keys(newCache);
            if (cacheKeys.length > 10) {
                const oldestKey = cacheKeys[0];
                const { [oldestKey]: _, ...rest } = newCache;
                return rest;
            }

            return newCache;
        });

        setClientSideTableData(pageData);

    }, [workerData.filteredData, currentPage, perPage]);

    // Efecto para inicializar primera página
    useEffect(() => {
        //console.log("UseEffect" + searchValue);
        if (!workerData || !workerData.filteredData || workerData.filteredData.length === 0) return;

        setClientSideTotal(workerData.filteredData.length);
        setClientSideTotalPages(Math.ceil(workerData.filteredData.length / perPage));

        const cacheKey = `page_1_size_${perPage}_search_${searchValue}_vehicle_${selectedVehicle || 'all'}_alert_${selectedAlertType || 'all'}`;

        if (!pagesCache[cacheKey]) {
            const pageData = workerData.filteredData.slice(0, perPage);
            setPagesCache(prev => ({ ...prev, [cacheKey]: pageData }));
            setClientSideTableData(pageData);
        } else {
            setClientSideTableData(pagesCache[cacheKey]);
        }
    }, [workerData?.filteredData, perPage, searchValue, selectedVehicle, selectedAlertType]);


    useEffect(() => {
        const handleApiError = (errorMessage, context) => {
            console.error(`❌ Error en ${context}:`, errorMessage);

            if (errorMessage.includes('rango de fechas') ||
                errorMessage.includes('no puede ser mayor') ||
                errorMessage.includes('días')) {
                setValidationError(errorMessage);
            } else if (errorMessage.includes('conexión') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('red') ||
                errorMessage.includes('ETIMEDOUT') ||
                errorMessage.includes('INTERNAL_SERVER_ERROR')) {
                setApiError(errorMessage);
            } else {
                setApiError(errorMessage);
            }

            setIsLoading(false);
        };

        const loadAlertsReport = async (filtros) => {
            try {
                setIsLoading(true);
                clearErrors();

                // Validaciones
                if (!filtros.uids_vehiculo || filtros.uids_vehiculo.length === 0) {
                    setValidationError('Debe seleccionar al menos un vehículo');
                    setIsLoading(false);
                    return;
                }
                if (!filtros.fecha_inicio || !filtros.fecha_fin) {
                    setValidationError('Debe seleccionar fechas válidas');
                    setIsLoading(false);
                    return;
                }

                const fechaInicio = dayjs(filtros.fecha_inicio);
                const fechaFin = dayjs(filtros.fecha_fin);
                const diferenciaDias = fechaFin.diff(fechaInicio, 'day');
                if (diferenciaDias > 31) {
                    setValidationError('El rango de fechas no puede ser mayor a 31 días');
                    setIsLoading(false);
                    return;
                }
                if (diferenciaDias < 0) {
                    setValidationError('La fecha de inicio no puede ser posterior a la fecha de fin');
                    setIsLoading(false);
                    return;
                }

                setTipoReporte(filtros.tipo_reporte);

                /*
                                const fechaInicioBefore = fechaInicio.subtract(1, 'month');
                                const fechaFinBefore = fechaFin.subtract(1, 'month');
                                const filtrosBefore = {
                                    ...filtros,
                                    fecha_inicio: fechaInicioBefore,
                                    fecha_fin: fechaFinBefore
                                };
                                */

                // Preparar filtros históricos
                let connectionId, apiUrl; //beforeConnectionId, 
                if (filtros.tipo_reporte === 'gps') {
                    connectionId = 'alertas-gps';
                    //beforeConnectionId = 'alertas-gps-before';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alerts`;
                } else if (filtros.tipo_reporte === 'camara') {
                    connectionId = 'alertas-camara';
                    //beforeConnectionId = 'alertas-camara-before';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alertsV2/obtener-reporte-alertas-ia-v2`;
                } else if (filtros.tipo_reporte === 'omisiones') {
                    connectionId = 'alertas-omisiones';
                    //beforeConnectionId = 'alertas-omisiones-before';
                    apiUrl = process.env.REACT_APP_OMISIONES_V3_API_URL;
                }

                const filtroGenerado = generarFiltro(filtros);
                /*
                    filtroGeneradoBefore = generarFiltro(filtrosBefore);
                */

                //console.log('📡 [Reporte Alertas] Petición principal:', {
                //    endpoint: apiUrl,
                //    connectionId,
                //    tipoReporte: filtros.tipo_reporte,
                //    filtros: filtroGenerado
                //});

                updateDebugMetrics({
                    connectionId,
                    startTime: Date.now(),
                    status: 'downloading'
                });

                // --- DATOS ACTUALES ---
                // Esperar a que realmente haya datos usando onPartialData
                const waitForData = () => new Promise((resolve, reject) => {
                    let resolved = false;
                    cargarPaginasUltraOptimizado(
                        apiUrl,
                        paginadorAlertas,
                        filtroGenerado,
                        connectionId,
                        (data, progress, error) => {
                            if (!resolved && Array.isArray(data) && data.length > 0) {
                                //console.log('📥 [Reporte Alertas] Respuesta principal:', {
                                //    endpoint: apiUrl,
                                //    registrosRecibidos: data.length,
                                //    primerRegistro: data,
                                //    camposDisponibles: Object.keys(data[0] || {})
                                //});
                            }
                            if (error && !resolved) {
                                resolved = true;
                                reject(error);
                            }
                            if (Array.isArray(data) && data.length > 0 && !resolved) {
                                resolved = true;
                                resolve(data);
                            }
                        },
                        (updates) => updateDebugMetrics({ ...updates, connectionId })
                    );
                });

                let datosActuales;
                try {
                    datosActuales = await waitForData();
                } catch (error) {
                    handleApiError(error, 'datos principales');
                    updateDebugMetrics({
                        connectionId,
                        endTime: Date.now(),
                        status: 'failed'
                    });
                    setIsLoading(false);
                    return;
                }

                if (!datosActuales || datosActuales.length === 0) {
                    console.log('[DEBUG] No hay datos principales, NO se lanza histórico');
                    setIsLoading(false);
                    updateDebugMetrics({
                        connectionId,
                        endTime: Date.now(),
                        status: 'completed'
                    });
                    return;
                }
                //console.log('[DEBUG] Hay datos principales, lanzando histórico...');

                updateDebugMetrics({
                    connectionId,
                    endTime: Date.now(),
                    status: 'completed'
                });
                /*
                // --- DATOS HISTÓRICOS SOLO SI LOS ACTUALES FUERON OK ---
                if (filtroGeneradoBefore !== null) {
                    updateDebugMetrics({
                        connectionId: beforeConnectionId,
                        startTime: Date.now(),
                        status: 'downloading'
                    });

                    await cargarPaginasUltraOptimizado(
                        apiUrl,
                        paginadorAlertasBefore,
                        filtroGeneradoBefore,
                        beforeConnectionId,
                        null,
                        (updates) => updateDebugMetrics({ ...updates, connectionId: beforeConnectionId })
                    );

                    updateDebugMetrics({
                        connectionId: beforeConnectionId,
                        endTime: Date.now(),
                        status: paginadorAlertasBefore.error ? 'failed' : 'completed'
                    });
                    console.log("📊 [DEBUG] Datos históricos descargados:", paginadorAlertasBefore.datos.length);
                }
                */

                setIsLoading(false);
                updateDebugMetrics({
                    searchEndTime: Date.now()
                });

            } catch (error) {
                handleApiError(error.message || 'Error desconocido', 'general');
                setIsLoading(false);
                updateDebugMetrics({
                    searchEndTime: Date.now()
                });
            }
        };

        if (
            filters &&
            (filters.tipo_reporte === 'gps' ||
                filters.tipo_reporte === 'camara' ||
                filters.tipo_reporte === 'omisiones') &&
            filters.uids_vehiculo &&
            filters.uids_vehiculo.length > 0 &&
            searchInitiated
        ) {
            loadAlertsReport(filters);
        }
    }, [filters, generarFiltro, handleClearFilters, updateDebugMetrics, clearErrors, searchInitiated]);

    useEffect(() => {
        const handleApiError = (errorMessage, context) => {
            console.error(`❌ Error en ${context}:`, errorMessage);

            if (errorMessage.includes('rango de fechas') ||
                errorMessage.includes('no puede ser mayor') ||
                errorMessage.includes('días')) {
                setValidationError(errorMessage);
            } else if (errorMessage.includes('conexión') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('red') ||
                errorMessage.includes('ETIMEDOUT') ||
                errorMessage.includes('INTERNAL_SERVER_ERROR')) {
                setApiError(errorMessage);
            } else {
                setApiError(errorMessage);
            }

            setIsLoading(false);
        };
        const loadAlertsAfterReport = async (filtros) => {
            try {
                clearErrors();
                const fechaInicioAfter = dayjs(filtros.fecha_inicio);
                const fechaFinAfter = dayjs(filtros.fecha_fin);
                const diferenciaDias = fechaFinAfter.diff(fechaInicioAfter, 'day');
                if (diferenciaDias > 31) {
                    setValidationError('El rango de fechas no puede ser mayor a 31 días');
                    setIsLoading(false);
                    return;
                }
                if (diferenciaDias < 0) {
                    setValidationError('La fecha de inicio no puede ser posterior a la fecha de fin');
                    setIsLoading(false);
                    return;
                }

                // Preparar filtros históricos
                let afterConnectionId, apiUrl;
                if (filtros.tipo_reporte === 'gps') {
                    afterConnectionId = 'alertas-gps-after';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alerts`;
                } else if (filtros.tipo_reporte === 'camara') {
                    afterConnectionId = 'alertas-camara-after';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alertsV2/obtener-reporte-alertas-ia-v2`;
                } else if (filtros.tipo_reporte === 'omisiones') {
                    afterConnectionId = 'alertas-omisiones-after';
                    apiUrl = process.env.REACT_APP_OMISIONES_V3_API_URL;
                }
                /* AFTER */

                const filtroGenerado = generarFiltro(filtros);

                console.log('📡 [Reporte Alertas] Petición AFTER:', {
                    endpoint: apiUrl,
                    connectionId: afterConnectionId,
                    tipoReporte: filtros.tipo_reporte,
                    filtros: filtroGenerado
                });

                updateDebugMetricsFrequency({
                    connectionId: afterConnectionId,
                    startTime: Date.now(),
                    status: 'downloading'
                });

                editarTotalCargaFrecuenciaAfter({
                    ...prevDP,
                    title: 'Periodo actual',
                    percent: 0,
                    color: "#2c7be5"
                });
                // --- DATOS ACTUALES ---
                // Esperar a que realmente haya datos usando onPartialData
                const waitForData = () => new Promise((resolve, reject) => {
                    let resolved = false;
                    cargarPaginasUltraOptimizado(
                        apiUrl,
                        paginadorAlertasAfter,
                        filtroGenerado,
                        afterConnectionId,
                        (data, progress, error) => {
                            /*if (error && !resolved) {
                                resolved = true;
                                reject(error);
                            }
                            if (Array.isArray(data) && data.length > 0 && !resolved) {
                                resolved = true;
                                resolve(data);
                            }*/
                        },
                        (updates) => {
                            editarTotalCargaFrecuenciaAfter({
                                ...prevDP,
                                title: 'Periodo actual',
                                percent: updates.downloadProgress,
                                color: "#2c7be5"
                            });
                            updateDebugMetricsFrequency({ ...updates, connectionId: afterConnectionId })
                        }
                    );
                });

                let datosActuales;
                try {
                    datosActuales = await waitForData();
                } catch (error) {
                    handleApiError(error, 'datos principales');
                    updateDebugMetricsFrequency({
                        connectionId: afterConnectionId,
                        endTime: Date.now(),
                        status: 'failed'
                    });
                    setIsLoading(false);
                    return;
                }

                if (!datosActuales || datosActuales.length === 0) {
                    console.log('[DEBUG] No hay datos principales, NO se lanza histórico');
                    setIsLoading(false);
                    updateDebugMetricsFrequency({
                        connectionId: afterConnectionId,
                        endTime: Date.now(),
                        status: 'completed'
                    });
                    return;
                }
                //console.log('[DEBUG] Hay datos principales, lanzando histórico...');

                updateDebugMetricsFrequency({
                    connectionId: afterConnectionId,
                    endTime: Date.now(),
                    status: 'completed'
                });

                updateDebugMetricsFrequency({
                    searchEndTime: Date.now()
                });

            } catch (error) {
                handleApiError(error.message || 'Error desconocido', 'general');
                updateDebugMetricsFrequency({
                    searchEndTime: Date.now()
                });
            }
        };
        const loadAlertsBeforeReport = async (filtros) => {
            try {
                clearErrors();
                const fechaInicioAfter = dayjs(filtros.fecha_inicio);
                const fechaFinAfter = dayjs(filtros.fecha_fin);
                const diferenciaDias = fechaFinAfter.diff(fechaInicioAfter, 'day');
                if (diferenciaDias > 31) {
                    setValidationError('El rango de fechas no puede ser mayor a 31 días');
                    setIsLoading(false);
                    return;
                }
                if (diferenciaDias < 0) {
                    setValidationError('La fecha de inicio no puede ser posterior a la fecha de fin');
                    setIsLoading(false);
                    return;
                }

                // Preparar filtros históricos
                let beforeConnectionId, apiUrl;
                if (filtros.tipo_reporte === 'gps') {
                    beforeConnectionId = 'alertas-gps-before';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alerts`;
                } else if (filtros.tipo_reporte === 'camara') {
                    beforeConnectionId = 'alertas-camara-before';
                    apiUrl = `${process.env.REACT_APP_ALERTS_API_URL}/alertsV2/obtener-reporte-alertas-ia-v2`;
                } else if (filtros.tipo_reporte === 'omisiones') {
                    beforeConnectionId = 'alertas-omisiones-before';
                    apiUrl = process.env.REACT_APP_OMISIONES_V3_API_URL;
                }
                /* BEFORE */
                const fechaInicioBefore = dayjs(filtros.fecha_inicio_before);
                const fechaFinBefore = dayjs(filtros.fecha_fin_before);
                if (diferenciaDias > 31) {
                    setValidationError('El rango de fechas no puede ser mayor a 31 días');
                    setIsLoading(false);
                    return;
                }
                if (diferenciaDias < 0) {
                    setValidationError('La fecha de inicio no puede ser posterior a la fecha de fin');
                    setIsLoading(false);
                    return;
                }

                const filtrosBefore = {
                    ...filtros,
                    fecha_inicio: fechaInicioBefore,
                    fecha_fin: fechaFinBefore
                };
                let filtroGeneradoBefore = generarFiltro(filtrosBefore);

                // --- DATOS HISTÓRICOS SOLO SI LOS ACTUALES FUERON OK ---
                if (filtroGeneradoBefore !== null) {
                    console.log('📡 [Reporte Alertas] Petición BEFORE:', {
                        endpoint: apiUrl,
                        connectionId: beforeConnectionId,
                        tipoReporte: filtros.tipo_reporte,
                        filtros: filtroGeneradoBefore
                    });

                    updateDebugMetricsFrequency({
                        connectionId: beforeConnectionId,
                        startTime: Date.now(),
                        status: 'downloading'
                    });

                    editarTotalCargaFrecuenciaBefore({
                        ...prevDP,
                        title: 'Periodo anterior',
                        percent: 0,
                        color: "#f5803e"
                    });
                    await cargarPaginasUltraOptimizado(
                        apiUrl,
                        paginadorAlertasBefore,
                        filtroGeneradoBefore,
                        beforeConnectionId,
                        (data, progress, error) => {
                            /*if (error && !resolved) {
                                resolved = true;
                                reject(error);
                            }
                            if (Array.isArray(data) && data.length > 0 && !resolved) {
                                resolved = true;
                                resolve(data);
                            }*/
                        },
                        (updates) => {
                            editarTotalCargaFrecuenciaBefore({
                                ...prevDP,
                                title: 'Periodo anterior',
                                percent: updates.downloadProgress,
                                color: "#f5803e"
                            });
                            updateDebugMetricsFrequency({ ...updates, connectionId: beforeConnectionId })
                        }
                    );

                    updateDebugMetricsFrequency({
                        connectionId: beforeConnectionId,
                        endTime: Date.now(),
                        status: paginadorAlertasBefore.error ? 'failed' : 'completed'
                    });
                    console.log("📊 [DEBUG] Datos históricos descargados:", paginadorAlertasBefore.datos.length);
                }


                updateDebugMetricsFrequency({
                    searchEndTime: Date.now()
                });

            } catch (error) {
                handleApiError(error.message || 'Error desconocido', 'general');
                updateDebugMetricsFrequency({
                    searchEndTime: Date.now()
                });
            }
        };
        if (store.filtrosAfter !== null && store.filtrosBefore !== null) {
            let debugDefaultFrequency = {
                searchStartTime: null,
                searchEndTime: null,

                connections: {
                    'alertas-gps': {
                        name: 'Datos Actuales (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-after': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-gps-before': {
                        name: 'Datos Históricos (GPS)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara': {
                        name: 'Datos Actuales (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-after': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-camara-before': {
                        name: 'Datos Históricos (IA)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones': {
                        name: 'Datos Actuales (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-after': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                    'alertas-omisiones-before': {
                        name: 'Datos Históricos (Omisiones)',
                        startTime: null,
                        endTime: null,
                        totalRecordsExpected: 0,
                        totalRecordsReceived: 0,
                        totalApiCalls: 0,
                        completedApiCalls: 0,
                        failedApiCalls: 0,
                        recoveredApiCalls: 0,
                        apiCallsHistory: [],
                        downloadProgress: 0,
                        status: 'pending'
                    },
                },

                globalTotalRecordsExpected: 0,
                globalTotalRecordsReceived: 0,
                globalDownloadProgress: 0,
                activeConnections: 0,

                componentTimes: {
                    'Periodo Actual': { start: null, end: null },
                    'Periodo Anterior': { start: null, end: null },
                }
            };
            setDebugMetricsFrequency(debugDefaultFrequency);
            updateComponentFrequencyProgress('Periodo Actual', 0, false);
            updateComponentFrequencyProgress('Periodo Anterior', 0, false);


            editarTotalCargaFrecuenciaAfter({
                ...prevDP,
                title: 'Periodo actual',
                percent: 0,
                color: "#2c7be5"
            });
            editarTotalCargaFrecuenciaBefore({
                ...prevDP,
                title: 'Periodo anterior',
                percent: 0,
                color: "#f5803e"
            });
            editarTotalMapeoFrecuenciaAfter(prevDP);
            editarTotalMapeoFrecuenciaBefore(prevDP);
            loadAlertsAfterReport({
                ...filters,
                fecha_inicio: store.filtrosAfter.fecha_inicio,
                fecha_fin: store.filtrosAfter.fecha_fin,
                fecha_inicio_before: store.filtrosBefore.fecha_inicio,
                fecha_fin_before: store.filtrosBefore.fecha_fin,
            });
            loadAlertsBeforeReport({
                ...filters,
                fecha_inicio: store.filtrosAfter.fecha_inicio,
                fecha_fin: store.filtrosAfter.fecha_fin,
                fecha_inicio_before: store.filtrosBefore.fecha_inicio,
                fecha_fin_before: store.filtrosBefore.fecha_fin,
            });
            store.editarFiltrosAfter(null);
            store.editarFiltrosBefore(null);
        }
    }, [store, updateDebugMetricsFrequency]);

    useEffect(() => {
        if (paginadorAlertas.error) {
            // Evitar ciclo infinito: solo actualizar si el error es diferente
            if (apiError !== paginadorAlertas.error && validationError !== paginadorAlertas.error) {
                console.error("🚨 Error detectado en paginador:", paginadorAlertas.error);
                const errorMsg = paginadorAlertas.error;

                if (errorMsg.includes('ETIMEDOUT') ||
                    errorMsg.includes('timeout') ||
                    errorMsg.includes('INTERNAL_SERVER_ERROR') ||
                    errorMsg.includes('conexión')) {
                    setApiError(`Error de conexión: ${errorMsg}`);
                } else if (errorMsg.includes('rango de fechas') ||
                    errorMsg.includes('días')) {
                    setValidationError(errorMsg);
                } else {
                    setApiError(errorMsg);
                }
                setIsLoading(false);
            }
        }
    }, [paginadorAlertas.error, apiError, validationError]);


    // Efecto para generar reportes cuando cambian los paginadores
    useEffect(() => {
        const generarReporteAlerta = async () => {
            // 🔍 DEBUG: Ver qué llega del endpoint de alertas
            if (paginadorAlertas.datos?.length > 0) {
                console.log("🔍 [Alertas] Datos crudos del endpoint - total:", paginadorAlertas.datos.length);
                console.log("📋 [Alertas] Primer registro - keys:", Object.keys(paginadorAlertas.datos[0]));
                console.log("📋 [Alertas] Primer registro - data:", paginadorAlertas.datos[0]);
                console.log("🔑 [Alertas] Campo random:", paginadorAlertas.datos[0]?.random, "| Campo codigoaleatorio:", paginadorAlertas.datos[0]?.codigoaleatorio);
            }
            const processedAlerts = paginadorAlertas.datos.map(alert => {
                const alertConTitulo = agregarAlertaTitulo(alert);

                return {
                    ...alertConTitulo,
                    chofer: alertConTitulo.chofer || 'N/A',
                    geocerca: alertConTitulo.geocerca || 'N/A'
                };
            });
            setAlerts(processedAlerts);
            //setAlerts(paginadorAlertas.datos.map(agregarAlertaTitulo));
        };

        generarReporteAlerta();

        if (paginadorAlertas.datos.length > 0) {
            const porcentajeFinal = paginadorAlertas.progresoCargado;
            editarTotalCarga([{
                title: 'Total de reporte cargado',
                percent: porcentajeFinal,
                color: "#c00f1b",
                animated: true
            }]);
        }

    }, [paginadorAlertas]);

    useEffect(() => {

        const generarReporteAlertaAfter = async () => {
            setAlertsAfter(paginadorAlertasAfter.datos.map(agregarAlertaTitulo));
        };

        const generarReporteAlertaBefore = async () => {
            setAlertsBefore(paginadorAlertasBefore.datos.map(agregarAlertaTitulo));
        };

        if (paginadorAlertasAfter.datos.length > 0) {
            generarReporteAlertaAfter();
        }

        if (paginadorAlertasBefore.datos.length > 0) {
            generarReporteAlertaBefore();
        }

        if (paginadorAlertasAfter.datos.length > 0 || paginadorAlertasBefore.datos.length > 0) {
            const porcentajeFinal = (paginadorAlertasAfter.progresoCargado + paginadorAlertasBefore.progresoCargado) / 2;
            /*editarTotalCargaFrecuencia({
                title: 'Descargando datos',
                percent: porcentajeFinal,
                color: "#c00f1b",
                animated: true
            });*/
        }

    }, [paginadorAlertasAfter, paginadorAlertasBefore]);

    // ============================================================================
    // DATOS FILTRADOS PARA LA UI
    // ============================================================================
    let filteredAlerts = alerts;
    let filteredAlertsByVehicle = alerts;
    let filteredAlertsAfter = alertsAfter;
    let filteredAlertsBefore = alertsBefore;

    if (selectedVehicle) {
        filteredAlerts = filteredAlerts.filter(alert => alert.no_economico === selectedVehicle.id);
        filteredAlertsByVehicle = filteredAlertsByVehicle.filter(alert => alert.no_economico === selectedVehicle.id);
        filteredAlertsAfter = filteredAlertsAfter.filter(alert => alert.no_economico === selectedVehicle.id);
        filteredAlertsBefore = filteredAlertsBefore.filter(alert => alert.no_economico === selectedVehicle.id);
    }

    if (selectedAlertType) {
        filteredAlerts = filteredAlerts.filter(alert =>
            selectedVehicle ? alert.alerta === selectedAlertType && alert.no_economico === selectedVehicle.id
                : alert.alerta === selectedAlertType
        );
        filteredAlertsAfter = filteredAlertsAfter.filter(alert =>
            selectedVehicle ? alert.alerta === selectedAlertType && alert.no_economico === selectedVehicle.id
                : alert.alerta === selectedAlertType
        );
        filteredAlertsBefore = filteredAlertsBefore.filter(alert =>
            selectedVehicle ? alert.alerta === selectedAlertType && alert.no_economico === selectedVehicle.id
                : alert.alerta === selectedAlertType
        );
    }

    const MySwal = withReactContent(Swal);


    // ============================================================================
    // Renderizado del componente
    // ============================================================================
    return (

        //Fragment
        <>
            {/* Cabecera del reoprte */}
            <div className='m-3'>
                <Row>
                    {/* Titulo del modulo */}
                    <Col md={10}>
                        <h4 className="mt-2">Alertas</h4>
                    </Col>

                </Row>
            </div>

            {/* CARD: Formulario de filtros */}
            <Card className="p-3 mb-3 mt-3">
                {/* <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Filtros Alerta</h6>
                </div> */}

                {/* Divisor para espaciado */}
                <hr className="text-danger" />

                {/*console.log('🔄 [ReporteAlertas-Render] Pasando initialReportType:', initialReportType)*/}

                {/* Formulario; Filtros del reporte */}
                <ReporteAlertasFiltros
                    onResetForm={handleResetReport}
                    onGenerateReport={handleGenerateReport}
                    initialReportType={initialReportType}
                    imprimir={!searchInitiated ? (
                        false
                    ) : isLoading ? (
                        false
                    ) : shouldShowEmptyState() ? false : true}
                />

                {/* Formulario; Metricas del reporte */}
                {/* <DebugMetricsPanel
                    debugMetrics={debugMetrics}
                    loadingComponents={loadingComponents}
                    isVisible={showDebugPanel}
                    onToggle={() => setShowDebugPanel(false)}
                    title="📊 Métricas de Reporte de Alertas"
                /> */}

                <Col md={12}>
                    {(paginadorAlertas.cargando) ? (
                        // ✅ USAR StandardProgressBar PARA LA DESCARGA PRINCIPAL
                        <StandardProgressBar
                            calculateTotalProgress={() => {
                                // Calcular progreso basado en las conexiones de descarga
                                if (!debugMetrics || !debugMetrics.connections) return 0;

                                const activeConnections = Object.values(debugMetrics.connections).filter(conn =>
                                    conn.status === 'downloading' || conn.status === 'completed'
                                );

                                if (activeConnections.length === 0) return 0;

                                const totalProgress = activeConnections.reduce((sum, conn) =>
                                    sum + (conn.downloadProgress || 0), 0
                                );

                                return totalProgress / activeConnections.length;
                            }}
                            debugMetrics={debugMetrics}
                            hideDelay={1500} // Ocultar después de 1.5 segundos
                        />
                    ) : (
                        // ✅ MANTENER ProgressBarWithComponents PARA EL PROCESAMIENTO DE UI
                        isLoading && (Object.values(loadingComponents).some(c => c.loading) && (
                            <ProgressBarWithComponents
                                loadingComponents={loadingComponents}
                                progress={maxProgress}
                                calculateTotalProgress={calculateTotalProgress}
                            />)
                        )
                    )}
                </Col>

            </Card>

            {/* Mensaje para mostrar errores y advertencias */}
            {validationError && (
                <ValidationError
                    message={validationError}
                    onClose={() => setValidationError(null)}
                />
            )}

            {/* Mensaje para mostrar errores y advertencias del consumo de apis */}
            {apiError && (
                <APIError
                    message={apiError}
                    onClose={() => setApiError(null)}
                    onRetry={() => {
                        setApiError(null);
                        if (filters) {
                            console.log('🔄 Reintentando después de error...');
                            clearErrors();
                        }
                    }}
                />
            )}

            {/* Alerta para manejo de errores*/}
            {errorMessage && !validationError && !apiError && (
                <AlertError
                    message={errorMessage}
                    title="Error al generar reporte"
                    onClose={() => setErrorMessage(null)}
                />
            )}

            {/* Renderizado condicional optimizado  */}
            {!searchInitiated ? (
                <WelcomeEmptyState reportType="alertas" />
            ) : isLoading ? (
                <LoadingEmptyState reportType="alertas" />
            ) : shouldShowEmptyState() ? (
                getEmptyStateProps().component
            ) : (
                <>
                    {/* ✅ ALERTAS GPS *******************************************************/}
                    {tipoReporte === 'gps' && alerts.length > 0 && (
                        <div className="g-3">

                            {/* FILA 1: ALERTAS POR VEHICULOS + ALERTAS DETONADAS */}
                            <Row className="g-3 mb-3">

                                {/* CARD ALERTAS POR VEHÍCULOS - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="vehicles"
                                            loadingState={loadingComponents.vehicles}
                                            data={workerData.vehiculoDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaVehiculo
                                                data={workerData.vehiculoDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectVehicle={handleSelectVehicle}
                                                highlightedVehicle={highlightedVehicle?.title}
                                                vehiculoFiltrado={selectedVehicle?.title}
                                                handleFiltro={handleClearFilters}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>

                                {/* CARD ALERTAS DETONADAS (CHART + TABLA DESPLEGABLE) - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="alerts"
                                            loadingState={loadingComponents.alerts}
                                            data={workerData.chartDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertasDetonadas
                                                data={workerData.chartDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectAlertType={handleSelectAlertType}
                                                highlightedAlertType={highlightedAlertType}
                                                vehiculoFiltrado={selectedVehicle}
                                                alertaFiltrada={selectedAlertType}
                                                handleFiltro={handleFiltroDetonados}
                                                verDetalle={verDetalle}
                                                setVerDetalle={setVerDetalle}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 2: MAPA UBICACION + GRAFICA COMPARATIVA*/}
                            <Row className="g-3 mb-3">

                                {/* MAPA UBICACION (50% ANCHO) */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="map"
                                            loadingState={loadingComponents.map}
                                            data={workerData.mapData || []}
                                            skeletonType="map"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaUbicacion
                                                data={workerData.mapData}
                                                isGroup={true}
                                            />
                                        </SmartComponentRenderer>

                                    </div>
                                </Col>

                                {/* GRAFICA COMPARATIVA (50% ANCHO) */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="frequency"
                                            loadingState={loadingComponents.frequency}
                                            data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AnimatedComponentWrapper>
                                                <AlertaFrecuencia
                                                    data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                                    dataHours={store.firstLoad === true ? workerData.frecuenciaHoraDataGeneral || [] : workerData.frecuenciaHoraDataAfter || []}
                                                    dataBefore={workerData.frecuenciaDataBefore || []}
                                                    dataBeforeHours={workerData.frecuenciaHoraDataBefore || []}
                                                    total={store.firstLoad === true ? workerData.total || 0 : workerData.totalAfterGeneral || 0}
                                                    totalBefore={workerData.totalBeforeGeneral || 0}
                                                    isSingleDay={store.firstLoad === true ? workerData.isSingleDay || false : workerData.isSingleDayAfter || false}
                                                    isSingleDayBefore={workerData.isSingleDayBefore || false}
                                                    title="GPS"
                                                    alertaFiltrada={selectedAlertType}
                                                    vehiculoFiltrado={selectedVehicle}
                                                    isLoading={paginadorAlertasAfter.cargando || paginadorAlertasBefore.cargando}
                                                    loadingComponents={loadingComponentsFrequency}
                                                    calculateTotalProgress={calculateTotalProgressFrequency}
                                                    debugMetrics={debugMetricsFrequency}
                                                    dataProgress={[totalCargaFrecuenciaAfter, totalmapeoFrecuenciaAfter, totalCargaFrecuenciaBefore, totalmapeoFrecuenciaBefore]}
                                                />
                                            </AnimatedComponentWrapper>
                                        </SmartComponentRenderer>

                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 3: TABLA ALERTAS GPS */}
                            <Row className="g-3">
                                <Col md={12}>

                                    <div style={{ height: '700px' }}>
                                        <SmartComponentRenderer
                                            component="table"
                                            loadingState={loadingComponents.table}
                                            data={tableData || []}
                                            skeletonType="table"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaTableListTanstack
                                                data={tableData.sort((a, b) => dayjs(a.fecha) - dayjs(b.fecha))}
                                                columns={columns}
                                                searchValue={searchValue}
                                                onSearch={setSearchValue}
                                                pageSize={perPage}
                                                onPageSizeChange={handlePageSizeChange}
                                                isLoading={false}
                                                title="Listado Alertas GPS"
                                                onTableRendered={(count) => {
                                                    //console.log("🔔 Tabla renderizada con", count, "registros");
                                                }}
                                                showExtendedColumns={showExtendedColumns}
                                            />
                                        </SmartComponentRenderer>
                                    </div>

                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* ✅ ALERTAS CÁMARAS IA ************************************************/}
                    {tipoReporte === 'camara' && alerts.length > 0 && (
                        <div className="g-3">
                            {/* FILA 1: Vehículos + Alertas Detonadas */}
                            <Row className="g-3 mb-3">
                                {/* VEHÍCULOS - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="vehicles"
                                            loadingState={loadingComponents.vehicles}
                                            data={workerData.vehiculoDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaVehiculo
                                                data={workerData.vehiculoDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectVehicle={handleSelectVehicle}
                                                highlightedVehicle={highlightedVehicle}
                                                vehiculoFiltrado={selectedVehicle}
                                                handleFiltro={handleClearFilters}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>

                                {/* ALERTAS DETONADAS - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="alerts"
                                            loadingState={loadingComponents.alerts}
                                            data={workerData.chartDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertasDetonadas
                                                data={workerData.chartDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectAlertType={handleSelectAlertType}
                                                highlightedAlertType={highlightedAlertType}
                                                vehiculoFiltrado={selectedVehicle}
                                                alertaFiltrada={selectedAlertType}
                                                handleFiltro={handleFiltroDetonados}
                                                verDetalle={verDetalle}
                                                setVerDetalle={setVerDetalle}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 2: Mapa + Frecuencia */}
                            <Row className="g-3 mb-3">
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="map"
                                            loadingState={loadingComponents.map}
                                            data={workerData.mapData || []}
                                            skeletonType="map"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaUbicacion
                                                data={workerData.mapData}
                                                isGroup={true}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>

                                {/* FRECUENCIA - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="frequency"
                                            loadingState={loadingComponents.frequency}
                                            data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AnimatedComponentWrapper>
                                                <AlertaFrecuencia
                                                    data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                                    dataHours={store.firstLoad === true ? workerData.frecuenciaHoraDataGeneral || [] : workerData.frecuenciaHoraDataAfter || []}
                                                    dataBefore={workerData.frecuenciaDataBefore || []}
                                                    dataBeforeHours={workerData.frecuenciaHoraDataBefore || []}
                                                    total={store.firstLoad === true ? workerData.total || 0 : workerData.totalAfterGeneral || 0}
                                                    totalBefore={workerData.totalBeforeGeneral || 0}
                                                    isSingleDay={store.firstLoad === true ? workerData.isSingleDay || false : workerData.isSingleDayAfter || false}
                                                    isSingleDayBefore={workerData.isSingleDayBefore || false}
                                                    title="Cámaras"
                                                    alertaFiltrada={selectedAlertType}
                                                    vehiculoFiltrado={selectedVehicle}
                                                    isLoading={paginadorAlertasAfter.cargando || paginadorAlertasBefore.cargando}
                                                    loadingComponents={loadingComponentsFrequency}
                                                    calculateTotalProgress={calculateTotalProgressFrequency}
                                                    debugMetrics={debugMetricsFrequency}
                                                    dataProgress={[totalCargaFrecuenciaAfter, totalmapeoFrecuenciaAfter, totalCargaFrecuenciaBefore, totalmapeoFrecuenciaBefore]}
                                                /></AnimatedComponentWrapper>
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 3: Tabla - 100% ancho */}
                            <Row className="g-3">
                                <Col md={12}>
                                    <div style={{ height: '700px' }}>
                                        <SmartComponentRenderer
                                            component="table"
                                            loadingState={loadingComponents.table}
                                            data={tableData || []}
                                            skeletonType="table"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaTableListTanstack
                                                data={tableData.sort((a, b) => dayjs(a.fecha) - dayjs(b.fecha))}
                                                columns={columns}
                                                searchValue={searchValue}
                                                onSearch={setSearchValue}
                                                pageSize={perPage}
                                                onPageSizeChange={handlePageSizeChange}
                                                isLoading={false}
                                                title="Listado Alertas IA"
                                                onTableRendered={(count) => {
                                                    //console.log("🔔 Tabla renderizada con", count, "registros");
                                                }}
                                                showExtendedColumns={showExtendedColumns}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* ✅ ALERTAS OMISIONES  ************************************************/}
                    {tipoReporte === 'omisiones' && alerts.length > 0 && (
                        <div className="g-3">
                            {/* FILA 1: Vehículos + Alertas Detonadas */}
                            <Row className="g-3 mb-3">
                                {/* VEHÍCULOS - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="vehicles"
                                            loadingState={loadingComponents.vehicles}
                                            data={workerData.vehiculoDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaVehiculo
                                                data={workerData.vehiculoDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectVehicle={handleSelectVehicle}
                                                highlightedVehicle={highlightedVehicle?.title}
                                                vehiculoFiltrado={selectedVehicle?.title}
                                                handleFiltro={handleClearFilters}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>

                                {/* ALERTAS DETONADAS - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '400px' }}>
                                        <SmartComponentRenderer
                                            component="alerts"
                                            loadingState={loadingComponents.alerts}
                                            data={workerData.chartDataGeneral || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertasDetonadas
                                                data={workerData.chartDataGeneral || []}
                                                total={workerData.totalGeneral || 0}
                                                onSelectAlertType={handleSelectAlertType}
                                                highlightedAlertType={highlightedAlertType}
                                                vehiculoFiltrado={selectedVehicle}
                                                alertaFiltrada={selectedAlertType}
                                                handleFiltro={handleFiltroDetonados}
                                                verDetalle={verDetalle}
                                                setVerDetalle={setVerDetalle}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 2: Mapa + Frecuencia */}
                            <Row className="g-3 mb-3">
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="map"
                                            loadingState={loadingComponents.map}
                                            data={workerData.mapData || []}
                                            skeletonType="map"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AlertaUbicacion
                                                data={workerData.mapData || []}
                                                isGroup={true}
                                            />
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>

                                {/* FRECUENCIA - 50% ancho, altura fija */}
                                <Col md={6} xs={12}>
                                    <div style={{ height: '500px' }}>
                                        <SmartComponentRenderer
                                            component="frequency"
                                            loadingState={loadingComponents.frequency}
                                            data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                            skeletonType="chart"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <AnimatedComponentWrapper>
                                                <AlertaFrecuencia
                                                    data={store.firstLoad === true ? workerData.frecuenciaDataGeneral || [] : workerData.frecuenciaDataAfter || []}
                                                    dataHours={store.firstLoad === true ? workerData.frecuenciaHoraDataGeneral || [] : workerData.frecuenciaHoraDataAfter || []}
                                                    dataBefore={workerData.frecuenciaDataBefore || []}
                                                    dataBeforeHours={workerData.frecuenciaHoraDataBefore || []}
                                                    total={store.firstLoad === true ? workerData.total || 0 : workerData.totalAfterGeneral || 0}
                                                    totalBefore={workerData.totalBeforeGeneral || 0}
                                                    isSingleDay={store.firstLoad === true ? workerData.isSingleDay || false : workerData.isSingleDayAfter || false}
                                                    isSingleDayBefore={workerData.isSingleDayBefore || false}
                                                    title="Omisiones"
                                                    alertaFiltrada={selectedAlertType}
                                                    vehiculoFiltrado={selectedVehicle}
                                                    isLoading={paginadorAlertasAfter.cargando || paginadorAlertasBefore.cargando}
                                                    loadingComponents={loadingComponentsFrequency}
                                                    calculateTotalProgress={calculateTotalProgressFrequency}
                                                    debugMetrics={debugMetricsFrequency}
                                                    dataProgress={[totalCargaFrecuenciaAfter, totalmapeoFrecuenciaAfter, totalCargaFrecuenciaBefore, totalmapeoFrecuenciaBefore]}
                                                /></AnimatedComponentWrapper>
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>

                            {/* FILA 3: Tabla - 100% ancho */}
                            <Row className="g-3">
                                <Col md={12}>
                                    <div style={{ height: '700px' }}>
                                        <SmartComponentRenderer
                                            component="table"
                                            loadingState={loadingComponents.table}
                                            data={tableData || []}
                                            skeletonType="table"
                                            chunkSize={25}
                                            enableProgressive={false}
                                        >
                                            <div id="tabla-alertas">
                                                <AlertaTableListTanstack
                                                    data={tableData.sort((a, b) => dayjs(a.fecha) - dayjs(b.fecha))}
                                                    columns={columns}
                                                    searchValue={searchValue}
                                                    onSearch={setSearchValue}
                                                    pageSize={perPage}
                                                    onPageSizeChange={handlePageSizeChange}
                                                    isLoading={false}
                                                    title="Listado Alertas Omisiones"
                                                    isGroup={true}
                                                    onTableRendered={(count) => {
                                                        //console.log("🔔 Tabla renderizada con", count, "registros");
                                                    }}
                                                    showExtendedColumns={showExtendedColumns}
                                                />
                                            </div>
                                        </SmartComponentRenderer>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </>
            )}
        </>
    );

};

export default ReporteAlertas;